import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase, logActivity } from '../lib/supabase';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth } from '../contexts/AuthContext';
import AddendumsModal from '../components/AddendumsModal';
import ServiceOrderPreviewModal from '../components/ServiceOrderPreviewModal';
import EngagementPanel from '../components/EngagementPanel';
// v3.5.99.7.13: ETF Calculator (per-quote)
import ETFCalculatorModal from '../components/ETFCalculatorModal';
import { loadAddendumTemplates, validateAddendums } from '../lib/addendums';
// v3.5.67: per-user quote-type permissions
import { userCanCreateQuoteType } from '../lib/quotePermissions';
import { lookupZip, fmt$, fmt$0, fmtPct, gmColor, gmBg } from '../lib/pricing';
import { calcVoice, calcHybridMRR, getRecommendedTier, getCXTiers, getLightsailCost, getLightsailLabel, CX_TIERS, FAX_PACKAGES, suggestFaxPackage, getFaxPackages, loadVoiceHardwareCatalog, loadVoiceCXTiers } from '../lib/voicePricing';
// v3.5.92.2: unified totals utility — Voice migration (Phase 1 item 2, 3/4).
// See CLAUDE.md §10b. The §4a NRC split lands here: onboarding-incentive
// picker now operates on Programming & Configuration only (was previously
// the broader laborNrc which lumped Programming + Porting + Activation).
import { buildTotals } from '../lib/quoteTotals';
import { writeQuoteUrlToDeal, searchDeals, getDealFull, updateDealDescription, captureHubspotSnapshot, pushHubspotGapFill, pushHubspotOverwrite } from '../lib/hubspot';
import QuoteNotes    from '../components/QuoteNotes';
import QuoteHistory  from '../components/QuoteHistory';
import { saveQuoteVersion } from '../lib/quoteVersions';
import { ReviewBanner } from '../components/SendForReview';
import QuoteSaveBar from '../components/QuoteSaveBar';
import RepSelector from '../components/RepSelector';
import IntlDialingWaiver       from '../components/IntlDialingWaiver';
import VoiceAssumptionsModal  from '../components/VoiceAssumptionsModal';
import ByohPicker             from '../components/ByohPicker';
import LOAModal               from '../components/LOAModal';
import PortReadinessCard      from '../components/PortReadinessCard';
import OnboardingIncentive    from '../components/OnboardingIncentive';
import ManualDiscounts         from '../components/ManualDiscounts';
import OneTimeDiscounts        from '../components/OneTimeDiscounts';
import AgreementEffectiveDate  from '../components/AgreementEffectiveDate';
import { discountedResult }    from '../lib/manualDiscounts';
import { applyOneTimeDiscounts } from '../lib/oneTimeDiscounts';
// v3.8.3 — Unified-UX workspace shell (flag-gated ferrumiq_v3_voice, default off).
import { WorkspaceShell, QuoteHeader, LiveTotalCard, RailCard, SubCard, Button as V3Button } from '../components/v3';
import { isQuoteLocked, QUOTE_LOCK_REASON } from '../lib/quoteLock';
import LockedQuoteBanner from '../components/LockedQuoteBanner';
import QuoteDocuments from '../components/QuoteDocuments';
import { v3 } from '../lib/styleTokens';
import RateSheetModalComp     from '../components/RateSheetModal';
import StripePaymentCard      from '../components/StripePaymentCard';
import SignatureCard          from '../components/SignatureCard';
import HubSpotConnect from '../components/HubSpotConnect';
import SPTConnect    from '../components/SPTConnect';
import MarketRateCard from '../components/MarketRateCard';
import MarketStatusBadge from '../components/MarketStatusBadge';
import { staticTierToMILabel, tierColor, tierBg } from '../lib/marketRates';

const DEF = {
  quoteType: 'hosted', licenseType: 'pro',
  seats: 0, seatPrice: 0, seatCost: 0,
  commonAreaPhones: 0, voicemailOnly: 0, doorPhones: 0, pagingDevices: 0, specialRingers: 0,
  cxTierId: 'pro_8', clientPaysMonthly: true, isManagedIT: false, largerInstance: false,
  sipChannels: 0,
  localDIDs: 0, smsDIDs: 0, tollFreeNumbers: 0, e911DIDs: 0, tollFreePerMin: false, tollFreePerMinRate: 0.05, portingDIDList: '',
  faxType: 'none', faxUsers: 1, faxDIDs: 1, ataItems: [],
  callRecording: false, callRecordingDays: 30,
  smsEnabled: false, smsNewRegistration: true, smsCampaigns: 1, smsCampaignList: [],
  hardwareType: 'none', hardwareItems: [], hardwareDiscount50: false, byohItems: [],
  waiveProgrammingFee: false, portingNumbers: 0,
  contractTerm: 24,
  internationalDialing: 'none',
};

export default function VoiceQuotePage() {
  const { id } = useParams();
  const { settings, marketTiers, loading: configLoading } = useConfig();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // v3.5.67: quote-type permission gate (see QuotePage for full rationale).
  // v3.5.69: moved below useNavigate — see QuotePage v3.5.69 comment for full
  // explanation of the TDZ error this avoids.
  const _isNewQuote = !id;
  const _hasPermission = userCanCreateQuoteType(profile, 'voice');
  const isReadOnly = !_isNewQuote && profile != null && !_hasPermission;
  useEffect(() => {
    if (_isNewQuote && profile != null && !_hasPermission) {
      navigate('/quotes', { replace: true, state: { permissionDenied: 'voice' } });
    }
  }, [profile, _isNewQuote, _hasPermission, navigate]);

  // ── Handle conversion from Bundle (unbundle → Voice) ─────────────────────
  useEffect(() => {
    const from = location.state?.fromBundle;
    if (!from || from.type !== 'voice' || configLoading) return;
    setRecipientBiz(from.clientName       || '');
    setProposalName(from.proposalName     || '');
    setRecipientContact(from.recipientContact || '');
    setRecipientEmail(from.recipientEmail     || '');
    setRecipientAddress(from.recipientAddress || '');
    setRecipientPhone(from.recipientPhone || '');
    setRecipientMobilePhone(from.recipientMobilePhone || '');
    setHubContactId(from.hubspotContactId || '');
    setHubCompanyId(from.hubspotCompanyId || '');
    setHubspotSnapshot(from.hubspotSnapshot || null);
    setNotificationCc(from.notificationCc || '');
    setClientZip(from.clientZip || '');
    setDealDescription(from.notes || '');
    setHubDealId(from.hubDealId   || '');
    setHubDealUrl(from.hubDealUrl  || '');
    setHubDealName(from.hubDealName || '');
    if (from.voiceInputs) setV(prev => ({ ...prev, ...from.voiceInputs }));
  }, [location.state, configLoading]);

  const [v, setV]               = useState(DEF);
  const [proposalName, setProposalName] = useState('');
  const [sptProposalId,    setSptProposalId]    = useState(null);
  const [showIntlWaiver,      setShowIntlWaiver]      = useState(false);
  const [showVoiceAssumptions, setShowVoiceAssumptions] = useState(false);
  // v3.5.86.10: page-level mirror of inputs.voiceAssumptions.story so the
  // A&E doc card can render saved/missing state without re-querying the DB
  // every render. Hydrated from inputs.voiceAssumptions.story in the load
  // .then block; updated by VoiceAssumptionsModal's onSaved callback (the
  // modal already passes the saved object as the callback argument). The
  // doc card UI then derives `voiceAssumptionsComplete` from this and
  // applies the same teal/amber visual vocabulary that v3.5.86.3.1
  // established for IT/Multi-Site/Bundle (§7o).
  const [voiceAssumptionsStory, setVoiceAssumptionsStory] = useState('');
  const [showLOA,              setShowLOA]              = useState(false);
  const [loaDocRecord,         setLoaDocRecord]         = useState(null);
  const [intlWaiverDocRecord,  setIntlWaiverDocRecord]  = useState(null);
  const [faxPackagesDB,        setFaxPackagesDB]        = useState([]);
  // v3.5.23: voice hardware catalog from voice_hardware table.
  // Initialized to the deprecated constants so the dropdown isn't empty
  // during the brief window between mount and the async fetch resolving.
  const [hardwareCatalog,      setHardwareCatalog]      = useState({ yealink: [], atas: [] });
  // v3.5.77: 3CX license tier catalog from voice_cx_tiers DB. Empty array on
  // mount; loadVoiceCXTiers() populates. Pricing engine (calcVoice and
  // calcHybridMRR) falls back to the CX_TIERS_BASE constant in voicePricing.js
  // if this stays empty (transient fetch failure or table not yet seeded).
  const [cxTiersDB,            setCxTiersDB]            = useState([]);
  const [voiceProgIncentive,   setVoiceProgIncentive]   = useState(null);
  // v3.5.86.4: Voice now supports the same onboarding incentive picker as
  // QuotePage / MultiSiteQuotePage / BundleQuotePage. The incentive applies
  // to the full Voice NRC (handsets, activation, porting, setup) — same
  // treatment Bundle uses for its combined IT+Voice NRC. Stored on the
  // shared `onboarding_incentive` JSON column. Hydrated on load below;
  // persisted on save in the payload below; consumed by StripePaymentCard
  // via the prepayAmount prop.
  const [obIncentive,          setObIncentive]          = useState(null);
  // v3.5.99.7.23: manual discount lines [{ type:'pct'|'fixed', value, note }]
  const [manualDiscounts,      setManualDiscounts]      = useState([]);
  const [oneTimeDiscounts,     setOneTimeDiscounts]     = useState([]);
  const [voiceFocusedSection,  setVoiceFocusedSection]  = useState(null);  // v3.8.3 workspace
  // v3.5.99.7.24: optional back-dated agreement effective date (YYYY-MM-DD)
  const [effectiveDate,        setEffectiveDate]        = useState('');
  const [pricingSnapshot, setPricingSnapshot] = useState(null);
  const [priceLockDate,   setPriceLockDate]   = useState(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  // ── v3.5.83.2.1: Lock-Rates-First UI guard ────────────────────────────
  // Mirrors QuotePage.js's v3.5.83.2 implementation, scoped to Voice. The
  // v3.5.82.4.1 backend block at /netlify/functions/sendServiceOrder rejects
  // Service Order generation on quotes without locked OOS rates (HTTP 400,
  // code:'rates_not_locked'). This client-side guard surfaces the issue
  // BEFORE the rep clicks Send via three coordinated surfaces:
  //   1. Yellow banner at top of right panel (when saved + unlocked + pre-snapshot)
  //   2. Yellow border + ⚠ on Send buttons inside VoiceSignatureCard
  //   3. Confirmation modal when those buttons are clicked while unlocked
  //      ("Lock Rates Now" scrolls to the OOS card via id="oos-rate-card";
  //       "Send Anyway" calls signatureCardRef.openSendModal() to bypass)
  // acceptedRates: hydrated on load from pricing_snapshot.acceptedRates and
  // updated live by MarketRateCard's onRatesAccepted. Used for ratesLocked
  // derivation AND folded into pricing_snapshot at approval-time save.
  const [acceptedRates,      setAcceptedRates]      = useState(null);
  const [showLockRatesModal, setShowLockRatesModal] = useState(null); // null | 'send_signature'
  const signatureCardRef = useRef(null);
  const [repId,        setRepId]        = useState(null);
  const [repProfile,   setRepProfile]   = useState(null);
  const [teamMembers,  setTeamMembers]  = useState([]);
  const [recipientBiz, setRecipientBiz] = useState('');
  const [recipientContact, setRecipientContact] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  // v3.5.99.7.3: client phone/mobile pulled from HubSpot in connectDeal.
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientMobilePhone, setRecipientMobilePhone] = useState('');
  // v3.5.99.7.4: HubSpot write-back state. contactId/companyId route
  // updateContact/updateCompany calls. snapshot captures HubSpot's original
  // values at connectDeal time so gap-fill knows which fields to push.
  const [hubContactId, setHubContactId] = useState('');
  const [hubCompanyId, setHubCompanyId] = useState('');
  const [hubspotSnapshot, setHubspotSnapshot] = useState(null);
  // v3.5.99.7.13: ETF Calculator modal visibility
  const [showEtfModal, setShowEtfModal] = useState(false);
  // v3.5.99.7.6: manual full-overwrite push state. Distinct from the
  // existing hubMsg/hubSyncing state (which is QuotePage-only and used
  // by the older syncToDeal button). hubPushing/hubPushMsg track the
  // new pushAllToHubspot flow.
  const [hubPushing, setHubPushing] = useState(false);
  const [hubPushMsg, setHubPushMsg] = useState('');

  // v3.5.99.7.6: manual full-overwrite push to HubSpot. Unlike the
  // automatic gap-fill (which only pushes fields HubSpot was originally
  // missing), this pushes ALL current Ferrum values, overwriting whatever
  // is in HubSpot. Use when rep wants to force HubSpot to match Ferrum's
  // current state — typically after correcting a phone number or address
  // in Ferrum that HubSpot has stale.
  async function pushAllToHubspot() {
    if (!hubContactId && !hubCompanyId) {
      setHubPushMsg('No HubSpot deal linked — connect a deal first.');
      return;
    }
    setHubPushing(true); setHubPushMsg('');
    try {
      const r = await pushHubspotOverwrite({
        contactId: hubContactId, companyId: hubCompanyId,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      });
      if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
      const total = r.contactFields.length + r.companyFields.length;
      if (total > 0) {
        setHubPushMsg(`✓ Pushed ${total} field${total > 1 ? 's' : ''} (${r.contactFields.length} contact, ${r.companyFields.length} company)`);
      } else {
        setHubPushMsg('Nothing to push — all recipient fields are empty.');
      }
      if (r.errors?.length) {
        console.warn('HubSpot push errors:', r.errors);
        setHubPushMsg(prev => prev + ' · ⚠ some errors (see console)');
      }
    } catch (e) {
      setHubPushMsg('✗ ' + (e.message || 'Push failed'));
    }
    setHubPushing(false);
  }
  // v3.5.99.0: per-quote override for proposal-approved notification CC list.
  const [notificationCc, setNotificationCc] = useState('');
  // v3.5.99.4: per-quote addendums — see QuotePage canonical block for rationale.
  const [addendums,         setAddendums]         = useState([]);
  const [addendumTemplates, setAddendumTemplates] = useState([]);
  const [addendumsModalOpen, setAddendumsModalOpen] = useState(false);
  // v3.5.99.5: Service Order preview modal — see QuotePage canonical block
  const [soPreviewModalOpen, setSoPreviewModalOpen] = useState(false);
  useEffect(() => { loadAddendumTemplates().then(setAddendumTemplates); }, []);
  const [clientZip,    setClientZip]    = useState('');
  const [zipResult,    setZipResult]    = useState(null);
  const [zipApplied,   setZipApplied]   = useState(false);
  const [selectedMkt,    setSelectedMkt]    = useState(null);
  const [marketAnalysis, setMarketAnalysis] = useState(null);
  const [showRateSheet,  setShowRateSheet]  = useState(false);
  const [marketCity,   setMarketCity]   = useState('');
  const [marketState, setMarketState] = useState('');
  const [dealDescription, setDealDescription]           = useState('');
  const [quoteStatus, setQuoteStatus] = useState('draft');
  const [saving, setSaving]         = useState(false);
  const [saveMsg, setSaveMsg]       = useState('');
  const [existingQuote, setExistingQuote] = useState(null);

  // HubSpot

  const [hubDealId, setHubDealId]   = useState('');
  const [hubDealName, setHubDealName] = useState('');
  const [hubDealUrl, setHubDealUrl] = useState('');

  const set = useCallback((k, val) => setV(p => ({ ...p, [k]: val })), []);

  // v3.5.91.2: human-readable Voice mode label used as package_name on
  // save AND as a display label in the HubSpot connect bar JSX below.
  // Matches src/lib/quoteDiff.js's existing voice-mode label map. Derived
  // from v.quoteType (state) so recomputes on render — cheap.
  const voiceLabel = (
    v.quoteType === 'hybrid' ? 'Hybrid Hosting' :
    v.quoteType === 'sip'    ? 'SIP Trunking'  :
                               'Hosted Seats'   // 'hosted' (default)
  );

  // ── Rep ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email, commission_rate').order('full_name')
      .then(({ data }) => setTeamMembers(data || []));
  }, []);

  // ── Voice Fax Packages — DB-loaded once on mount, drives sell + cost ─────────
  useEffect(() => {
    supabase.from('voice_fax_packages').select('*').eq('active', true).order('sort_order')
      .then(({ data }) => setFaxPackagesDB(data || []));
  }, []);
  // ── Voice Hardware Catalog — DB-loaded once on mount ────────────────────────
  // If the fetch fails or returns empty, the dropdowns will be empty until
  // the DB issue is resolved (loadVoiceHardwareCatalog returns _fallback:true
  // with empty arrays in that case).
  useEffect(() => {
    loadVoiceHardwareCatalog().then(setHardwareCatalog);
  }, []);
  // v3.5.77: load 3CX license tier catalog from voice_cx_tiers. Same pattern
  // as the hardware catalog above. Falls back to CX_TIERS_BASE constant in
  // voicePricing.js if the fetch fails or the table is empty.
  useEffect(() => {
    loadVoiceCXTiers().then(setCxTiersDB);
  }, []);
  useEffect(() => {
    if (!repId && profile?.id && !id) setRepId(profile.id);
  }, [profile, id]);
  useEffect(() => {
    if (!repId || !teamMembers.length) return;
    const rep = teamMembers.find(m => m.id === repId);
    if (rep) setRepProfile(rep);
  }, [repId, teamMembers]);

  // Load existing quote
  useEffect(() => {
    if (!id || id === 'new' || configLoading) return;
    supabase.from('quotes').select('*').eq('id', id).single().then(({ data }) => {
      if (!data) return;
      setExistingQuote(data);
      setProposalName(data.inputs?.proposalName || '');
      setRecipientBiz(data.client_name || '');
      setRecipientContact(data.inputs?.recipientContact || '');
      setRecipientEmail(data.inputs?.recipientEmail || '');
      setRecipientAddress(data.inputs?.recipientAddress || '');
      setRecipientPhone(data.inputs?.recipientPhone || '');
      setRecipientMobilePhone(data.inputs?.recipientMobilePhone || '');
      setHubContactId(data.inputs?.hubspotContactId || '');
      setHubCompanyId(data.inputs?.hubspotCompanyId || '');
      setHubspotSnapshot(data.inputs?.hubspotSnapshot || null);
      setNotificationCc(data.inputs?.notificationCc || '');
      setAddendums(Array.isArray(data.inputs?.addendums) ? data.inputs.addendums : []);
      setManualDiscounts(Array.isArray(data.inputs?.manualDiscounts) ? data.inputs.manualDiscounts : []);
      setOneTimeDiscounts(Array.isArray(data.inputs?.oneTimeDiscounts) ? data.inputs.oneTimeDiscounts : []);
      setEffectiveDate(data.inputs?.effectiveDate || '');
      setClientZip(data.client_zip || '');
      setQuoteStatus(data.status || 'draft');
      setDealDescription(data.notes || '');
      setHubDealId(data.hubspot_deal_id || '');
      setHubDealUrl(data.hubspot_deal_url || '');
      setHubDealName(data.inputs?.hubspotDealName || '');
      // v3.5.86.4: hydrate onboarding incentive from the saved column. Same
      // pattern QuotePage/MultiSiteQuotePage/BundleQuotePage use. Stored on
      // the dedicated onboarding_incentive JSONB column, not inside inputs.
      if (data.onboarding_incentive?.mode) setObIncentive(data.onboarding_incentive);
      // v3.5.86.10: hydrate voiceAssumptionsStory so the A&E doc card
      // shows the right saved/missing state on initial render without
      // re-querying the DB. The modal also re-loads from DB when opened
      // (its own useEffect in VoiceAssumptionsModal.js), so this is just
      // for the doc card's visual indicator.
      setVoiceAssumptionsStory(data.inputs?.voiceAssumptions?.story || '');
      if (data.inputs?.voice) setV({ ...DEF, ...data.inputs.voice });
      // Restore LOA doc record for port readiness
      const loaDocs = (data.inputs?.signwellDocuments || []).filter(d => d.type === 'loa');
      if (loaDocs.length > 0) setLoaDocRecord(loaDocs[loaDocs.length - 1]);
      // Restore International Dialing Waiver doc record for status display
      const intlWaiverDocs = (data.inputs?.signwellDocuments || []).filter(d => d.type === 'intl_waiver');
      if (intlWaiverDocs.length > 0) setIntlWaiverDocRecord(intlWaiverDocs[intlWaiverDocs.length - 1]);
      if (data.market_tier && marketTiers.length) {
        const t = marketTiers.find(t => t.tier_key === data.market_tier);
        if (t) setSelectedMkt(t);
      }
      if (data.rep_id) setRepId(data.rep_id);
      if (data.pricing_snapshot) {
        setPricingSnapshot(data.pricing_snapshot);
        setPriceLockDate(data.price_locked_at);
        // v3.5.83.2.1: hydrate Lock-Rates-First state from the snapshot so
        // ratesLocked is true immediately on reopen for approved quotes.
        // Pre-approval, MarketRateCard re-fires onRatesAccepted on mount
        // when quote_rate_sheets has a row, which sets acceptedRates live.
        if (data.pricing_snapshot.acceptedRates) setAcceptedRates(data.pricing_snapshot.acceptedRates);
      }
      if (data.spt_proposal_id) setSptProposalId(data.spt_proposal_id);
      // Refetch market analysis for rate card
      if (data.client_zip) handleZipChange(data.client_zip);
    });
  }, [id, configLoading]);

  async function handleZipChange(val) {
    setClientZip(val);
    const zr = val.length >= 3 ? lookupZip(val) : null;
    setZipResult(zr);
    if (val.length === 5) {
      try {
        const { data } = await supabase
          .from('market_rate_analyses')
          .select('*')
          .or(`zip.eq.${val},zip_codes.cs.{${val}}`)
          .maybeSingle();
        if (data) setMarketAnalysis(data);
        else if (zr && selectedMkt) {
          // Build synthetic analysis from market tier
          setMarketAnalysis({ city: zr.name?.split(',')[0] || '', state: zr.state || '', market_tier: selectedMkt.tier_key, pricing_multiplier: selectedMkt.pricing_multiplier || 1, rates: selectedMkt.rates || {} });
        }
      } catch {}
    }
  }

  // HubSpot
  async function saveQuote() {
    // v3.5.99.4: block save if any addendum has unfilled required placeholders.
    // See QuotePage canonical block for rationale.
    const addendumErrors = validateAddendums(addendums, addendumTemplates);
    if (addendumErrors.length > 0) {
      const summary = addendumErrors.slice(0, 3).map(e => `\u2022 ${e.template_name}: ${e.placeholder_label || e.reason}`).join('\n');
      const more = addendumErrors.length > 3 ? `\n\u2026and ${addendumErrors.length - 3} more.` : '';
      alert(`Can't save \u2014 ${addendumErrors.length} addendum issue${addendumErrors.length === 1 ? '' : 's'}:\n\n${summary}${more}\n\nOpen Special Terms to fix or remove the addendum.`);
      return;
    }
    // v3.5.67: read-only gate — see QuotePage saveQuote for rationale.
    if (isReadOnly) {
      setSaveMsg('You don\'t have permission to save Voice quotes. This quote is view-only.');
      return;
    }
    // v3.9.9.2: post-signature lock — a signed agreement freezes the terms. Persist
    // ONLY internal notes; never rewrite inputs/totals/status on a signed quote.
    if (isQuoteLocked(existingQuote)) {
      setSaving(true); setSaveMsg('');
      try {
        if (existingQuote?.id) {
          await supabase.from('quotes').update({ notes: dealDescription }).eq('id', existingQuote.id);
          if (hubDealId && dealDescription) { try { await updateDealDescription(hubDealId, dealDescription); } catch { /* non-blocking */ } }
        }
        setSaveMsg('🔒 Locked — only internal notes were saved.');
      } catch (e) { setSaveMsg('✗ ' + (e?.message || 'Save failed')); }
      setSaving(false);
      return;
    }
    if (!recipientBiz.trim()) { setSaveMsg('Please enter a client name.'); return; }
    setSaving(true); setSaveMsg('');
    // v3.9.9: spread existing inputs FIRST so fields not listed here survive the
    // save — MOST IMPORTANTLY signwellDocuments (sent SO / LOA / IntlWaiver
    // tracking). Without this, any save after a document was sent wiped the
    // signwellDocuments array from the DB: the envelope still existed in SignWell
    // but the app (and the webhook that auto-charges on signature) lost the record.
    // Matches the protection FlexITQuotePage has had since v3.5.52.
    const allInputs = { ...(existingQuote?.inputs || {}), proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc, addendums, hubspotDealName: hubDealName, voice: v, manualDiscounts: manualDiscounts || [], oneTimeDiscounts: oneTimeDiscounts || [], effectiveDate: effectiveDate || null };
    const r = configLoading ? null : calcVoice(v, settings, faxPackagesDB, hardwareCatalog, cxTiersDB);
    // v3.5.92.2: totals construction migrated to src/lib/quoteTotals.js
    // (Phase 1 item 2, sub-deploy 3 of 4). Identity persistence — same
    // object flows into payload.totals AND saveQuoteVersion below,
    // eliminating the §3 shape-drift bug class. The §4a NRC split
    // (Programming & Configuration ONLY counts as onboarding labor) is
    // implemented via the four discrete buckets calcVoice now returns —
    // utility lifts them into the unified shape.
    const totals = buildTotals('voice', r, {
      obIncentive,
      contractTerm: v.contractTerm,
      manualDiscounts,
      oneTimeDiscounts,
    });
    const payload = {
      client_name: recipientBiz, client_zip: clientZip,
      market_tier: selectedMkt?.tier_key || null,
      package_name: voiceLabel,
      // v3.5.91.0: dual-write quote_type. Canonical key 'voice' matches
      // src/lib/quotePermissions.js QUOTE_TYPES + profiles.allowed_quote_types
      // + RLS. v3.5.91.2: this column is now the SOLE type discriminator
      // (package_name no longer carries the type prefix).
      quote_type: 'voice',
      status: quoteStatus, notes: dealDescription, inputs: allInputs,
      line_items: r?.lines || [], totals,
      hubspot_deal_id: hubDealId || null, hubspot_deal_url: hubDealUrl || null,
      rep_id:     repId || profile?.id || null,
      ...(quoteStatus === 'approved' && !pricingSnapshot ? { pricing_snapshot: { lockedAt: new Date().toISOString(), acceptedRates: acceptedRates || null }, price_locked_at: new Date().toISOString(), price_locked_by: profile?.id } : {}),
      ...(pricingSnapshot ? { pricing_snapshot: pricingSnapshot, price_locked_at: priceLockDate } : {}),
      // v3.5.86.4: persist the onboarding incentive when active. Same JSONB
      // shape QuotePage/MultiSiteQuotePage/BundleQuotePage write — keeping
      // shape uniform across quote types matters for QuotesPage's NRC
      // column lookup at q.onboarding_incentive?.effectiveFee.
      onboarding_incentive: obIncentive?.mode ? {
        mode:           obIncentive.mode,
        effectiveFee:   obIncentive.effectiveFee,
        monthlyAdd:     obIncentive.monthlyAdd   || 0,
        splitMonths:    obIncentive.splitMonths  || 0,
        discountPct:    obIncentive.discountPct  || 0,
        discountAmount: obIncentive.discountAmount || 0,
      } : null,
      updated_by: profile?.id,
    };
    if (!existingQuote) payload.created_by = profile?.id;
    const { data, error } = existingQuote
      ? await supabase.from('quotes').update(payload).eq('id', existingQuote.id).select().single()
      : await supabase.from('quotes').insert(payload).select().single();
    if (error) { setSaveMsg('Error: ' + error.message); setSaving(false); return; }

    // Push deal description to HubSpot if linked
    if (hubDealId && dealDescription) {
      try { await updateDealDescription(hubDealId, dealDescription); }
      catch (err) { console.warn('HubSpot description sync failed:', err.message); }
    }

    await logActivity({ action: existingQuote ? 'UPDATE' : 'CREATE', entityType: 'quote', entityId: data.id, entityName: recipientBiz, changes: { type: 'voice', mrr: totals.finalMRR } });
    // Write quote URL back to HubSpot deal if a field is configured
    if (hubDealId && data?.id) {
      try {
        const { data: fieldData } = await supabase.from('pricing_settings').select('value').eq('key','hubspot_quote_url_field').single();
        const fieldKey = fieldData?.value;
        if (fieldKey) {
          const quoteUrl = `${window.location.origin}/voice/${data.id}`;
          await writeQuoteUrlToDeal(hubDealId, quoteUrl, fieldKey);
        }
      } catch (e) { console.warn('HubSpot quote URL write failed:', e.message); }
    }

    await saveQuoteVersion({
      quoteId: data.id,
      quoteData: { client_name: recipientBiz, client_zip: clientZip, package_name: voiceLabel, status: quoteStatus },
      inputs: { proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc, addendums, voice: v },
      totals,
      lineItems: r?.lines || [],
      profile,
    });

    setSaveMsg(`Saved as ${data.quote_number}${hubDealId && dealDescription ? ' · HubSpot updated' : ''}`);
    // v3.5.99.7.4: gap-fill push to HubSpot. Fire-and-forget after save
    // success — pushes only fields where HubSpot was empty when connectDeal
    // pulled (per hubspotSnapshot). Errors logged but don't block UI.
    // The manual full-overwrite push button comes in v3.5.99.7.5.
    if ((hubContactId || hubCompanyId) && hubspotSnapshot) {
      pushHubspotGapFill({
        contactId: hubContactId, companyId: hubCompanyId, snapshot: hubspotSnapshot,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      }).then(r => {
        if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
        if (r.errors?.length) console.warn('HubSpot gap-fill errors:', r.errors);
      }).catch(e => console.warn('HubSpot gap-fill failed:', e));
    }
    setSaving(false);
    if (!existingQuote) navigate(`/voice/${data.id}`, { replace: true });
  }

  const resultRaw = configLoading ? null : calcVoice(v, settings, faxPackagesDB, hardwareCatalog, cxTiersDB);
  // v3.5.99.7.23: apply manual discounts to the live display result so the
  // headline MRR, GM, TCV, and tax estimate all reflect the net. The save
  // path above passes the RAW result (`r`) + manualDiscounts to buildTotals,
  // which applies the discount exactly once — no double-application.
  const result = discountedResult(resultRaw, manualDiscounts);

  // v3.5.92.2: hardwareNrc / laborNrc derivation moved into calcVoice itself.
  // The engine now returns four discrete NRC subcategory totals — see
  // src/lib/voicePricing.js where each line-push site increments the
  // appropriate bucket alongside the legacy `nrc` total. The §4a "Programming
  // & Configuration only" decision (May 10 2026) means the onboarding-
  // incentive picker now operates on `result.onboardingNrc` specifically —
  // NOT the broader pre-deploy `laborNrc = result.nrc - hardwareNrc` which
  // also included Number Porting carrier fees and license/10DLC pass-through.
  // The non-labor non-hardware NRC (porting + activation) now flows through
  // separately and is NOT discounted by the picker.
  //
  // Identity: result.nrc === onboardingNrc + hardwareNrc + portingNrc + activationNrc
  // (asserted in calcVoice in dev builds).
  const hardwareNrc   = Number(result?.hardwareNrc)   || 0;
  const onboardingNrc = Number(result?.onboardingNrc) || 0;   // Programming & Configuration ONLY (§4a)
  const portingNrc    = Number(result?.portingNrc)    || 0;
  const activationNrc = Number(result?.activationNrc) || 0;

  // v3.7.8: per-category bases for the one-time discount editor. Onboarding is
  // post-incentive (matches the prepay base below and buildTotals). resolved
  // drives the live net + the Stripe prepay total so the client is charged net.
  const oneTimeBases = {
    onboarding: (obIncentive?.effectiveFee != null ? Number(obIncentive.effectiveFee) : onboardingNrc),
    hardware:   hardwareNrc,
    activation: activationNrc,
    porting:    portingNrc,
  };
  const oneTimeResolved = applyOneTimeDiscounts(oneTimeBases, oneTimeDiscounts);
  // BEHAVIOR CHANGE vs v3.5.86.5 — v3.5.92.1: the picker's `fee` prop was
  // previously `laborNrc` (= nrc - hardwareNrc, which included Porting +
  // Activation). Now the picker discounts ONLY onboardingNrc (Programming
  // & Configuration). A Voice quote with Programming $200 + Porting $250 +
  // 10DLC $65 = $515 of "non-hardware NRC" used to have the full $515
  // discounted by a 50%-off picker; post-v3.5.92.2 only the $200 is
  // discounted. Customer pays more by ($250 + $65) × discountPct on quotes
  // with porting and 10DLC. Re-confirm pricing on any active Voice quote
  // with an incentive between deploy and the customer's next look.

  // Crossover analysis for hosted seats
  const crossover = v.quoteType === 'hosted' && v.seats > 0 && result
    ? calcHybridMRR(v.seats, v.licenseType, v.isManagedIT, parseFloat(settings.voice_support_bundle || 295), parseFloat(settings.voice_hosting_cost || 24), parseFloat(settings.sip_channel_rate || 19.95), v.sipChannels || Math.ceil(v.seats * 0.3), settings, cxTiersDB)
    : null;

  const gc = result ? gmColor(result.gm) : '#374151';
  // v3.5.65: local mktColor/mktBg maps removed — use tierColor() and tierBg()
  // from marketRates.js, which know about both modern (premium/standard/
  // adjusted/secondary) and legacy (major_metro/mid_market/small_market/rural)
  // tier keys. Centralizes color choices so badges look identical across
  // QuotePage, BundleQuotePage, VoiceQuotePage, and MarketRateCard.
  // v3.5.68: re-applied — v3.5.66 inadvertently regressed this when its base
  // copy of VoiceQuotePage was a pre-v3.5.65 snapshot.
  const gb = result ? gmBg(result.gm)    : '#f9fafb';

  const recommendedTier = v.quoteType === 'hosted' && v.seats > 0 ? getRecommendedTier(v.seats, v.licenseType, settings, cxTiersDB) : null;

  // v3.5.83.2.1: derived flag for the Lock-Rates-First UI guard. Mirrors
  // QuotePage.js's pattern — non-null object with at least one key matches
  // the v3.5.82.4.1 backend precondition shape exactly.
  const ratesLocked = !!(
    acceptedRates &&
    typeof acceptedRates === 'object' &&
    Object.keys(acceptedRates).length > 0
  );

  // v3.5.83.2.1: scroll helper for the "Lock Rates Now" action in the
  // confirmation modal and the inline banner button. Targets the
  // <div id="oos-rate-card"> wrapper around <MarketRateCard>.
  const scrollToOosRateCard = () => {
    const el = document.getElementById('oos-rate-card');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // v3.8.3 — Unified-UX workspace layout for Voice (flag ferrumiq_v3_voice,
  // DEFAULT OFF). Mirrors the Managed IT rollout: reuses every existing handler
  // and child component; the legacy two-pane return below is untouched, so with
  // the flag off behaviour is identical. The intricate config builders (fax /
  // ATA / hardware / SMS) are reused verbatim inside the focus panes; a SubCard
  // restyle of those inner inputs is the noted fast-follow. Enable per-browser
  // with localStorage ferrumiq_v3_voice='1' (or ?v3=1 preview / ?v3=0 force off).
  // ─────────────────────────────────────────────────────────────────────────
  const v3VoiceEnabled = (() => {
    try {
      if (typeof window !== 'undefined') {
        const q = new URLSearchParams(window.location.search);
        if (q.get('v3') === '1') return true;
        if (q.get('v3') === '0') return false;
        const stored = window.localStorage.getItem('ferrumiq_v3_voice');
        if (stored === '1') return true;
        if (stored === '0') return false;                     // persistent opt-out
        return true;   // v3.8.4 — Voice workspace is now the DEFAULT (Shaun 2026-08-04).
      }                //          Classic layout reachable via ?v3=0 / stored '0'.
    } catch (e) { /* disabled storage */ }
    return true;
  })();

  if (v3VoiceEnabled) {
    const activeSection = voiceFocusedSection || (v.seats > 0 || v.quoteType !== 'hosted' ? 'numbers' : 'seats');
    const statusVariant = ({ won: 'green', approved: 'green', sent: 'blue', lost: 'red', expired: 'red', in_review: 'amber', draft: 'amber' })[quoteStatus] || 'gray';
    const typeLabel = v.quoteType === 'hosted' ? `Hosted · ${v.licenseType === 'enterprise' ? 'Enterprise' : 'Pro'}` : v.quoteType === 'hybrid' ? 'Hybrid Hosting' : 'SIP Trunking';

    const secTitle = (t) => (<h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>{t}</h2>);
    const secSub = (t) => (<p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6, marginBottom: 4 }}>{t}</p>);

    const v3Sections = [
      { id: 'client',    label: 'Client',            value: recipientBiz || 'Not set', status: recipientBiz ? 'ok' : 'empty' },
      { id: 'mode',      label: 'Voice mode',        value: `${typeLabel} · ${v.contractTerm} mo`, status: 'ok' },
      { id: 'seats',     label: 'Seats & licensing', value: v.quoteType === 'hosted' ? `${v.seats || 0} seats${recommendedTier ? ` · ${recommendedTier.label}` : ''}` : 'Hosted only', status: 'ok' },
      { id: 'numbers',   label: 'Numbers & DIDs',    value: `${v.localDIDs || 0} local · ${v.smsDIDs || 0} SMS · ${v.tollFreeNumbers || 0} TF`, status: 'ok' },
      { id: 'porting',   label: 'Number porting',    value: (v.portingNumbers || 0) > 0 ? `${v.portingNumbers} numbers · ${(v.portingDIDList || '').split('\n').filter(l => l.trim()).length} listed` : 'None', status: (v.portingNumbers || 0) > 0 && (v.portingDIDList || '').split('\n').filter(l => l.trim()).length < v.portingNumbers ? 'warn' : 'ok' },
      { id: 'sms',       label: 'SMS / 10DLC',       value: (v.smsEnabled || (v.smsDIDs || 0) > 0) ? `${(v.smsCampaignList || []).length} campaign${(v.smsCampaignList || []).length === 1 ? '' : 's'}` : 'Not enabled', status: (v.smsDIDs || 0) > 0 && !v.smsEnabled ? 'warn' : 'ok' },
      { id: 'fax',       label: 'Fax & ATA',         value: v.faxType && v.faxType !== 'none' ? v.faxType : 'None', status: 'ok' },
      { id: 'recording', label: 'Call recording',    value: v.callRecording ? `Enabled · ${v.callRecordingDays || 30}d` : 'Disabled', status: 'ok' },
      { id: 'hardware',  label: 'Hardware',          value: ({ none: 'Apps only', lease: 'Lease', purchase: 'Purchase', byoh: 'BYOH' })[v.hardwareType] || 'Apps only', status: 'ok' },
      { id: 'pricing',   label: 'Pricing',           value: result ? `${fmt$0(result.finalMRR)}/mo` : '—', status: 'ok' },
      { id: 'documents', label: 'Documents & send',  value: existingQuote?.id ? 'Rates · SO · pay' : 'Save first', status: 'ok' },
      { id: 'terms',     label: 'Special terms',     value: `${addendums.length} addendum${addendums.length === 1 ? '' : 's'}`, status: addendums.length ? 'ok' : 'empty' },
      { id: 'engagement',label: 'Engagement',        value: existingQuote?.id ? 'View activity' : 'Not sent', status: 'ok' },
      { id: 'history',   label: 'History',           value: existingQuote?.id ? 'Versions' : '—', status: existingQuote?.id ? 'ok' : 'empty' },
    ];

    const v3Header = (
      <QuoteHeader
        id={existingQuote?.quote_number || 'New voice quote'}
        title={proposalName || recipientBiz || 'Hosted Voice quote'}
        status={quoteStatus}
        statusVariant={statusVariant}
        extraPills={hubDealId ? [{ variant: 'green', label: 'HubSpot linked' }] : []}
        savedLabel={saving ? 'Saving…' : (saveMsg || undefined)}
      >
        <V3Button variant="secondary" size="sm" onClick={() => setSoPreviewModalOpen(true)} disabled={!existingQuote?.id}>Preview SO</V3Button>
        <V3Button variant="dark" size="sm" onClick={saveQuote} disabled={saving || isReadOnly}>{saving ? 'Saving…' : 'Save'}</V3Button>
      </QuoteHeader>
    );

    // v3.9.8: always-visible Service Order status so the rep can see at a glance
    // that the agreement was sent, without opening the Documents section. Derived
    // from the saved signwellDocuments (accurate on load/reopen); the Documents &
    // send card remains the live, actionable surface.
    const latestSODoc = (() => {
      const docs = existingQuote?.inputs?.signwellDocuments || [];
      const agr = docs.filter(d => d && d.type === 'voice_quote' && String(d.status || '').toLowerCase() !== 'cancelled');
      if (!agr.length) return null;
      return agr.reduce((a, b) => new Date(b.created_at || 0) > new Date(a.created_at || 0) ? b : a);
    })();
    const soStatusStyle = {
      sent:      { bg:'#dbeafe', color:'#1e40af', label:'Sent — awaiting signature' },
      viewed:    { bg:'#fef3c7', color:'#92400e', label:'Viewed by client' },
      signed:    { bg:'#dcfce7', color:'#166534', label:'Signed' },
      completed: { bg:'#dcfce7', color:'#166534', label:'Signed & complete' },
      declined:  { bg:'#fee2e2', color:'#991b1b', label:'Declined' },
      expired:   { bg:'#fef3c7', color:'#92400e', label:'Expired' },
    };

    const v3Rail = (
      <>
        <LiveTotalCard
          headline={result ? fmt$0(result.finalMRR) : '—'}
          rows={result ? [
            // v3.9.6: net one-time (post incentive + one-time discount) — matches
            // the line-item summary, the Stripe prepay, and the Service Order.
            { label: 'One-time fees', value: fmt$0(oneTimeResolved?.finalNRC ?? result.nrc) },
            { label: 'Est. w/ tax',   value: fmt$0(result.finalMRR + result.estTax) },
            { label: 'Gross margin',  value: fmtPct(result.gm), color: v3.color.moneyPositive },
          ] : []}
        />
        {latestSODoc && (() => {
          const s = soStatusStyle[String(latestSODoc.status || '').toLowerCase()] || soStatusStyle.sent;
          return (
            <RailCard title="Service Order">
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <span style={{ padding:'3px 10px', borderRadius:v3.radius.pill, fontSize:12, fontWeight:700, background:s.bg, color:s.color }}>{s.label}</span>
              </div>
              <div style={{ marginTop:6, fontSize:12, color:v3.color.muted, lineHeight:1.5 }}>
                {latestSODoc.client_email && <div>Sent to {latestSODoc.client_email}</div>}
                {latestSODoc.created_at && <div>On {new Date(latestSODoc.created_at).toLocaleString()}</div>}
              </div>
              <div style={{ marginTop:6, fontSize:11, color:v3.color.faint }}>Manage in “Documents &amp; send” below.</div>
            </RailCard>
          );
        })()}
        {result && (
          <RailCard title="Cost model — monthly">
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}><span style={{ color: v3.color.muted }}>Total cost</span><span style={{ fontFamily: v3.font.mono, fontWeight: 600 }}>{fmt$0(result.totalCost)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 6px', background: gmBg(result.gm), borderRadius: 4, marginTop: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: gmColor(result.gm) }}>Implied Gross Margin</span>
              <span style={{ fontSize: 13, fontWeight: 700, fontFamily: v3.font.mono, color: gmColor(result.gm) }}>{fmtPct(result.gm)}</span>
            </div>
            {result.gm < 0.35 && <div style={{ marginTop: 4, fontSize: 11, color: v3.color.status.amberText }}>⚠ Below 35% — review pricing.</div>}
          </RailCard>
        )}
        {crossover && v.quoteType === 'hosted' && (
          <RailCard title="Hosted vs Hybrid — crossover">
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}><span style={{ color: v3.color.muted }}>This quote (per seat)</span><span style={{ fontFamily: v3.font.mono, fontWeight: 600 }}>{fmt$0(result.finalMRR)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}><span style={{ color: v3.color.muted }}>Hybrid option</span><span style={{ fontFamily: v3.font.mono, fontWeight: 600, color: crossover.totalMRR < result.finalMRR ? v3.color.status.amberText : v3.color.body }}>{fmt$0(crossover.totalMRR)}</span></div>
            <div style={{ marginTop: 6, fontSize: 12, color: crossover.totalMRR < result.finalMRR ? v3.color.status.amberText : v3.color.status.greenText }}>
              {crossover.totalMRR < result.finalMRR ? `⚡ Hybrid saves ${fmt$0(result.finalMRR - crossover.totalMRR)}/mo` : '✓ Per-seat is more competitive'}
            </div>
          </RailCard>
        )}
      </>
    );

    // Intricate config builders reused verbatim from the legacy layout (same
    // state/handlers) — kept identical to avoid re-authoring risk. SubCard
    // restyle of these inner builders is the noted fast-follow.
    const voiceFaxSecEl = (
      <SubCard eyebrow="Virtual Fax">
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:8 }}>
          <Fld lbl="Fax Users" hint="How many users need fax access">
            <NI v={v.faxUsers||1} s={val=>{set('faxUsers',val); const sug=suggestFaxPackage(val,v.faxDIDs,faxPackagesDB); if(sug&&v.faxType!=='none'&&v.faxType!=='email_only') set('faxType',sug);}}/>
          </Fld>
          <Fld lbl="Fax DID Numbers" hint="Dedicated fax lines needed">
            <NI v={v.faxDIDs||1} s={val=>{set('faxDIDs',val); const sug=suggestFaxPackage(v.faxUsers,val,faxPackagesDB); if(sug&&v.faxType!=='none'&&v.faxType!=='email_only') set('faxType',sug);}}/>
          </Fld>
        </div>
        <Fld lbl="Fax Package">
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            {(() => {
              const faxCatalog = getFaxPackages(faxPackagesDB);
              const opts = [{ v:'none', l:'No Fax', sell:null, cost:null, d:'' }];
              Object.entries(faxCatalog).forEach(([key, pkg]) => { opts.push({ v:key, l:pkg.label, sell:pkg.price, cost:pkg.cost||0, d:pkg.desc }); });
              return opts.map(opt => {
                const suggested = opt.v !== 'none' && opt.v !== 'email_only' && suggestFaxPackage(v.faxUsers||1, v.faxDIDs||1, faxCatalog) === opt.v;
                const selected  = v.faxType === opt.v;
                const gm = opt.sell > 0 ? ((opt.sell - opt.cost) / opt.sell) * 100 : null;
                const gmC = gm == null ? '#6b7280' : gm >= 50 ? '#065f46' : gm >= 25 ? '#92400e' : '#991b1b';
                return (
                  <div key={opt.v} onClick={()=>set('faxType',opt.v)} style={{ padding:'6px 8px', borderRadius:4, cursor:'pointer', border:`${selected?'2':'1'}px solid ${selected?'#0891b2':'#e5e7eb'}`, background:selected?'#ecfeff':'white', display:'flex', justifyContent:'space-between', alignItems:'center', gap:6 }}>
                    <div style={{ minWidth:0, flex:1 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:selected?'#0e7490':'#374151' }}>{opt.l}</span>
                      {suggested && !selected && <span style={{ fontSize:8, marginLeft:5, padding:'1px 5px', background:'#d1fae5', color:'#065f46', borderRadius:3, fontWeight:700 }}>suggested</span>}
                      {opt.d && <div style={{ fontSize:8, color:'#6b7280', marginTop:1 }}>{opt.d}</div>}
                    </div>
                    {opt.sell != null && (
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', flexShrink:0, gap:1 }}>
                        <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${opt.sell.toFixed(2)}/mo</span>
                        {gm != null && (<span style={{ fontSize:8, fontFamily:'DM Mono, monospace', color:gmC, fontWeight:700 }}>cost ${opt.cost.toFixed(2)} · {gm.toFixed(0)}% GM</span>)}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </Fld>
      </SubCard>
    );
    const voiceAtaSecEl = (
      <SubCard eyebrow="ATA Devices">
        <div style={{ fontSize:9, color:'#6b7280', marginBottom:6, lineHeight:1.5 }}>Analog Telephone Adapters connect legacy fax machines and analog phones to the hosted PBX. Hardware is one-time; service is billed monthly per device.</div>
        {(v.ataItems||[]).map((ata, i) => (
          <div key={i} style={{ padding:'8px', background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:5, marginBottom:6 }}>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr auto', gap:6, alignItems:'end' }}>
              <div>
                <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Model</div>
                <select value={ata.modelId||'ht802'} onChange={e=>{ const model = hardwareCatalog.atas.find(m=>m.id===e.target.value); const items = [...(v.ataItems||[])]; items[i] = { ...items[i], modelId: e.target.value, label: model?.label||e.target.value, hardware_nrc: model?.hardware_nrc??items[i].hardware_nrc, monthly: model?.monthly??items[i].monthly, ports: model?.ports||1 }; set('ataItems', items); }} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>
                  {hardwareCatalog.atas.map(m=><option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
              <div><div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Qty</div><input type="number" min="1" value={ata.qty||1} onChange={e=>{const items=[...(v.ataItems||[])];items[i]={...items[i],qty:+e.target.value};set('ataItems',items);}} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, textAlign:'center', outline:'none' }}/></div>
              <div><div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>HW NRC</div><input type="number" min="0" step="5" value={ata.hardware_nrc??65} onChange={e=>{const items=[...(v.ataItems||[])];items[i]={...items[i],hardware_nrc:+e.target.value};set('ataItems',items);}} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, textAlign:'center', outline:'none' }}/></div>
              <div><div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>/mo</div><input type="number" min="0" step="1" value={ata.monthly??15} onChange={e=>{const items=[...(v.ataItems||[])];items[i]={...items[i],monthly:+e.target.value};set('ataItems',items);}} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, textAlign:'center', outline:'none' }}/></div>
              <button onClick={()=>set('ataItems',(v.ataItems||[]).filter((_,j)=>j!==i))} style={{ padding:'4px 7px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:4, color:'#dc2626', fontSize:12, cursor:'pointer', marginBottom:1 }}>×</button>
            </div>
          </div>
        ))}
        <button onClick={()=>{ const def = hardwareCatalog.atas[0]; if (!def) return; set('ataItems',[...(v.ataItems||[]),{modelId:def.id,label:def.label,qty:1,hardware_nrc:def.hardware_nrc,monthly:def.monthly,ports:def.ports}]); }} style={{ padding:'5px 10px', background:'white', border:'1px dashed #93c5fd', borderRadius:4, fontSize:10, color:'#0369a1', cursor:'pointer', width:'100%', textAlign:'left' }}>+ Add ATA Device</button>
      </SubCard>
    );
    const voiceHardwareSecEl = (
      <SubCard eyebrow="Hardware">
        <Fld lbl="Hardware Option">
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {[['none','No Hardware — Apps only'],['lease','Evergreen Lease (monthly recurring)'],['purchase','Outright Purchase (one-time)'],['byoh','BYOH — Bring Your Own Handset']].map(([val,lbl]) => (
              <label key={val} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', padding:'4px 6px', borderRadius:4, border:`1px solid ${v.hardwareType===val?'#374151':'#e5e7eb'}`, background:v.hardwareType===val?'#f8fafc':'white' }}>
                <input type="radio" name="hwTypeV3" value={val} checked={v.hardwareType===val} onChange={()=>set('hardwareType',val)} style={{ accentColor:'#374151' }}/>
                <span style={{ fontSize:10, fontWeight:v.hardwareType===val?700:400, color:'#374151' }}>{lbl}</span>
              </label>
            ))}
          </div>
        </Fld>
        {(v.hardwareType==='lease'||v.hardwareType==='purchase') && (
          <Fld lbl="Phone Models — add as many as needed">
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {(v.hardwareItems||[]).map((item,i) => (
                <div key={i} style={{ display:'flex', gap:5, alignItems:'center' }}>
                  <select value={item.model} onChange={e=>{const items=[...(v.hardwareItems||[])];items[i]={...items[i],model:e.target.value};set('hardwareItems',items);}} style={{ flex:1, padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>
                    {hardwareCatalog.yealink.filter(m => v.hardwareType==='lease' ? m.lease_eligible !== false : m.purchase_eligible !== false).map(m=><option key={m.id} value={m.id}>{m.label} — {v.hardwareType==='lease'?`$${m.monthly}/mo`:`$${m.nrc}`} · {m.desc}</option>)}
                  </select>
                  <input type="number" min="1" value={item.qty||1} onChange={e=>{const items=[...(v.hardwareItems||[])];items[i]={...items[i],qty:+e.target.value};set('hardwareItems',items);}} style={{ width:52, padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', textAlign:'center', outline:'none' }}/>
                  <button onClick={()=>{const items=(v.hardwareItems||[]).filter((_,j)=>j!==i);set('hardwareItems',items);}} style={{ padding:'3px 6px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:3, color:'#dc2626', fontSize:12, cursor:'pointer' }}>×</button>
                </div>
              ))}
              <button onClick={()=>set('hardwareItems',[...(v.hardwareItems||[]),{model:'T33G',qty:1}])} style={{ padding:'4px 8px', background:'white', border:'1px dashed #d1d5db', borderRadius:4, fontSize:10, color:'#6b7280', cursor:'pointer', textAlign:'left' }}>+ Add phone model</button>
            </div>
          </Fld>
        )}
        {v.hardwareType==='byoh' && (
          <Fld lbl="BYOH Devices">
            <div style={{ fontSize:9, color:'#6b7280', marginBottom:8, lineHeight:1.5 }}>Search the 3CX compatibility database below. Each device is wiped and registered at ${parseFloat(settings?.voice_byoh_fee||20).toFixed(0)}/device NRC.</div>
            <ByohPicker devices={v.byohItems||[]} onChange={items => set('byohItems', items)} />
          </Fld>
        )}
      </SubCard>
    );

    const renderVoiceFocus = () => {
      switch (activeSection) {
        case 'client':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Client')}{secSub('Who the proposal is addressed to, and the linked HubSpot deal.')}
            <HubSpotConnect
              dealId={hubDealId} dealUrl={hubDealUrl} dealName={hubDealName} description={dealDescription}
              onDescriptionChange={setDealDescription} quoteNumber={existingQuote?.quote_number}
              mrr={result?.finalMRR} contractValue={result ? result.finalMRR * v.contractTerm + oneTimeResolved.finalNRC : 0}
              packageName="Hosted Voice" contractTerm={v.contractTerm} existingQuoteId={existingQuote?.id} clientName={recipientBiz}
              onConnect={full => {
                setHubDealId(full.dealId); setHubDealUrl(full.dealUrl); setHubDealName(full.deal.dealname);
                if (full.company) {
                  if (full.company.name) setRecipientBiz(full.company.name);
                  const addr = [full.company.address, full.company.address2, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', ');
                  if (addr) setRecipientAddress(addr);
                  if (full.company.zip) { setClientZip(full.company.zip); handleZipChange(full.company.zip); }
                } else {
                  const extracted = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim();
                  if (extracted) setRecipientBiz(extracted);
                }
                if (full.contact) {
                  const name = [full.contact.firstname, full.contact.lastname].filter(Boolean).join(' ');
                  if (name) setRecipientContact(name);
                  if (full.contact.email) setRecipientEmail(full.contact.email);
                }
                if (!proposalName && full.deal.dealname) setProposalName(`FerrumIT Hosted Voice — ${full.company?.name || full.deal.dealname}`);
              }}
              onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }}
            />
            <SPTConnect proposalId={sptProposalId} quoteId={existingQuote?.id} clientName={recipientBiz} quoteNumber={existingQuote?.quote_number} settings={settings} onConnect={(pid) => setSptProposalId(pid)} onDisconnect={() => setSptProposalId(null)} />
            <SubCard eyebrow="Proposal Details">
              <Fld lbl="Assigned Sales Rep"><RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} /></Fld>
              <Fld lbl="Proposal Name"><TI value={proposalName} onChange={setProposalName} placeholder="FerrumIT Hosted Voice — Acme Corp"/></Fld>
              <Fld lbl="Client Business Name"><TI value={recipientBiz} onChange={setRecipientBiz} placeholder="Acme Corp"/></Fld>
              <Grid2>
                <Fld lbl="Contact Name"><TI value={recipientContact} onChange={setRecipientContact} placeholder="Jane Smith"/></Fld>
                <Fld lbl="Contact Email"><TI value={recipientEmail} onChange={setRecipientEmail} placeholder="jane@acme.com"/></Fld>
              </Grid2>
              <Fld lbl="Business Address"><TI value={recipientAddress} onChange={setRecipientAddress} placeholder="123 Main St, Chicago, IL 60601"/></Fld>
              <Grid2>
                <Fld lbl="Office Phone"><TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567"/></Fld>
                <Fld lbl="Mobile Phone"><TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321"/></Fld>
              </Grid2>
              {(hubContactId || hubCompanyId) && (
                <div style={{ marginTop: 8 }}>
                  <V3Button variant="secondary" size="sm" onClick={pushAllToHubspot} disabled={hubPushing}>{hubPushing ? '⏳ Pushing…' : '↑ Push to HubSpot'}</V3Button>
                  {hubPushMsg && <span style={{ fontSize: 11, color: v3.color.muted, marginLeft: 8 }}>{hubPushMsg}</span>}
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                <Fld lbl="CC on Approval (comma-separated)"><TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com"/></Fld>
              </div>
              <Fld lbl="Zip Code">
                <input value={clientZip} onChange={e=>handleZipChange(e.target.value)} placeholder="60601" style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', outline:'none' }}/>
                {zipResult && <div style={{ fontSize:9, color:'#6b7280', marginTop:2 }}>{zipResult.name || `ZIP ${zipResult.zip}`}</div>}
              </Fld>
            </SubCard>
            <SubCard eyebrow="Market Tier">
              <div style={{ marginBottom: 6 }}><MarketStatusBadge aiMultiplier={null} aiMultiplierTier={null} selectedMkt={selectedMkt} marketCity={marketCity} marketState={marketState} locked={!!pricingSnapshot} compact={true}/></div>
              {marketTiers.map(t => (
                <div key={t.id} onClick={() => setSelectedMkt(t)} style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`${selectedMkt?.id===t.id?'2':'1'}px solid ${selectedMkt?.id===t.id?tierColor(t.tier_key):'#e5e7eb'}`, background:selectedMkt?.id===t.id?tierBg(t.tier_key):'white', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:10, fontWeight:700, color:tierColor(t.tier_key) }}>{staticTierToMILabel(t.tier_key)} <span style={{ fontWeight:400, color:'#9ca3af', fontSize:9 }}>({t.name})</span></span>
                </div>
              ))}
            </SubCard>
          </div>);
        case 'mode':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Voice mode')}{secSub('Deployment model, license, term, and trunking.')}
            <SubCard eyebrow="Contract & Status">
              <Grid2>
                <Fld lbl="Contract Term"><select value={v.contractTerm} onChange={e=>set('contractTerm',+e.target.value)} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none' }}><option value={12}>12 months</option><option value={24}>24 months</option><option value={36}>36 months</option></select></Fld>
                <Fld lbl="Quote Status"><select value={quoteStatus} onChange={e=>setQuoteStatus(e.target.value)} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none' }}>{['draft','in_review','approved','sent','won','lost','expired'].map(s=><option key={s} value={s}>{s}</option>)}</select></Fld>
              </Grid2>
            </SubCard>
            <SubCard eyebrow="Voice Quote Type">
              <div style={{ display:'grid', gap:3 }}>
                {[['hosted','Hosted Voice — Per Seat','Per-user hosted PBX on our platform'],['hybrid','Hybrid Hosting — BYOPBX','Client leases 3CX + we host + SIP trunking'],['sip','SIP Trunking Only','Concurrent call paths — client has own PBX']].map(([key,lbl,desc])=>(
                  <div key={key} onClick={()=>set('quoteType',key)} style={{ padding:'7px 9px', borderRadius:5, cursor:'pointer', border:`${v.quoteType===key?'2':'1'}px solid ${v.quoteType===key?'#7c3aed':'#e5e7eb'}`, background:v.quoteType===key?'#faf5ff':'white' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:v.quoteType===key?'#6d28d9':'#374151' }}>{lbl}</div>
                    <div style={{ fontSize:9, color:'#9ca3af', marginTop:1 }}>{desc}</div>
                  </div>
                ))}
              </div>
            </SubCard>
            {v.quoteType === 'hosted' && (
              <SubCard eyebrow="License Type">
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4 }}>
                  {[['pro','3CX Pro','Standard features'],['enterprise','3CX Enterprise','+ AI & transcription']].map(([key,lbl,desc])=>(
                    <div key={key} onClick={()=>set('licenseType',key)} style={{ padding:'6px 8px', borderRadius:4, cursor:'pointer', border:`${v.licenseType===key?'2':'1'}px solid ${v.licenseType===key?'#2563eb':'#e5e7eb'}`, background:v.licenseType===key?'#eff6ff':'white' }}>
                      <div style={{ fontSize:10, fontWeight:700, color:v.licenseType===key?'#1e40af':'#374151' }}>{lbl}</div>
                      <div style={{ fontSize:8, color:'#9ca3af' }}>{desc}</div>
                    </div>
                  ))}
                </div>
                {recommendedTier && (<div style={{ marginTop:6, padding:'5px 8px', background:'#f0f7ff', borderRadius:4, fontSize:9, color:'#1e40af' }}>Recommended: <strong>{recommendedTier.label}</strong> ({recommendedTier.ext_min}–{recommendedTier.ext_max} ext) — ${(recommendedTier.annual_cost/12).toFixed(2)}/mo amortized</div>)}
              </SubCard>
            )}
            {v.quoteType === 'hybrid' && (
              <SubCard eyebrow="3CX License & Hosting">
                <Fld lbl="3CX License Tier">
                  <select value={v.cxTierId} onChange={e=>set('cxTierId',e.target.value)} style={{ width:'100%', padding:'5px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none' }}>
                    {(() => {
                      const tiers = (Array.isArray(cxTiersDB) && cxTiersDB.length > 0) ? cxTiersDB : CX_TIERS;
                      const fmtCost = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                      return (<>
                        <optgroup label="3CX Pro">{tiers.filter(t => t.type === 'pro').map(t => (<option key={t.id} value={t.id}>{t.paths} paths ({t.ext_min}–{t.ext_max} ext) — ${fmtCost(t.annual_cost)}/yr</option>))}</optgroup>
                        <optgroup label="3CX Enterprise">{tiers.filter(t => t.type === 'enterprise').map(t => (<option key={t.id} value={t.id}>{t.paths} paths ({t.ext_min}–{t.ext_max} ext) — ${fmtCost(t.annual_cost)}/yr</option>))}</optgroup>
                      </>);
                    })()}
                  </select>
                </Fld>
                <Tog on={v.clientPaysMonthly} set={val=>set('clientPaysMonthly',val)} lbl="Amortize license monthly" sub="Auto-calculates annual ÷ 12"/>
                <Tog on={v.isManagedIT} set={val=>set('isManagedIT',val)} lbl="Managed IT client" sub="Support included — hosting only charged"/>
                <Tog on={v.largerInstance} set={val=>set('largerInstance',val)} lbl="Enhanced VM instance" sub="API/reporting integrations — higher compute"/>
              </SubCard>
            )}
            {(v.quoteType === 'sip' || v.quoteType === 'hybrid') && (
              <SubCard eyebrow="SIP Trunking"><Fld lbl="Concurrent Call Paths" sub="$19.95/path/mo"><NI v={v.sipChannels} s={val=>set('sipChannels',val)}/></Fld></SubCard>
            )}
          </div>);
        case 'seats':
          if (v.quoteType !== 'hosted') return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{secTitle('Seats & licensing')}{secSub('Per-seat configuration applies to Hosted Voice. Switch the mode to Hosted, or use the 3CX license tier under Voice mode.')}</div>);
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Seats & licensing')}{secSub('Billable seats, sell price, and the auto-calculated cost basis.')}
            <SubCard eyebrow="Seat Configuration">
              <Grid2>
                <Fld lbl="Billable Seats" sub="humans only"><NI v={v.seats} s={val=>set('seats',val)}/></Fld>
                <Fld lbl="Sell Price / Seat ($)"><NI v={v.seatPrice} s={val=>set('seatPrice',val)}/></Fld>
              </Grid2>
              {v.seats > 0 && settings && (
                <div style={{ marginTop:6, padding:'6px 9px', background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:4, fontSize:9, color:'#0369a1' }}>
                  <div style={{ fontWeight:700, marginBottom:3 }}>Ferrum Cost Breakdown (auto-calculated)</div>
                  {(() => {
                    const tier = getRecommendedTier(v.seats, v.licenseType||'pro', settings, cxTiersDB);
                    const lsCost = getLightsailCost(v.seats, settings);
                    const cxMo = tier ? tier.annual_cost / 12 : 0;
                    const totalCost = cxMo + lsCost;
                    const margin = v.seatPrice > 0 ? 1 - (totalCost / (v.seats * v.seatPrice)) : null;
                    return (<div>
                      <div style={{ display:'flex', justifyContent:'space-between' }}><span>3CX {tier?.label} (${ tier?.annual_cost || 0}/yr ÷ 12)</span><span style={{ fontFamily:'DM Mono, monospace' }}>${cxMo.toFixed(2)}/mo</span></div>
                      <div style={{ display:'flex', justifyContent:'space-between' }}><span>{getLightsailLabel(v.seats, settings)}</span><span style={{ fontFamily:'DM Mono, monospace' }}>${lsCost}/mo</span></div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, borderTop:'1px solid #bae6fd', marginTop:3, paddingTop:3 }}><span>Total Ferrum Cost</span><span style={{ fontFamily:'DM Mono, monospace' }}>${totalCost.toFixed(2)}/mo</span></div>
                      {margin !== null && (<div style={{ display:'flex', justifyContent:'space-between', color: margin >= 0.4 ? '#065f46' : margin >= 0.25 ? '#92400e' : '#991b1b', fontWeight:700, marginTop:2 }}><span>Implied Gross Margin</span><span style={{ fontFamily:'DM Mono, monospace' }}>{(margin*100).toFixed(1)}%</span></div>)}
                    </div>);
                  })()}
                </div>
              )}
            </SubCard>
            <SubCard eyebrow="Non-Billable Devices">
              <div style={{ fontSize:9, color:'#9ca3af', marginBottom:5 }}>Shown on quote at $0 — for transparency and provisioning</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:5 }}>
                <Fld lbl="Common Area Phones"><NI v={v.commonAreaPhones} s={val=>set('commonAreaPhones',val)}/></Fld>
                <Fld lbl="Voicemail Only Ext."><NI v={v.voicemailOnly} s={val=>set('voicemailOnly',val)}/></Fld>
                <Fld lbl="Door Phones"><NI v={v.doorPhones} s={val=>set('doorPhones',val)}/></Fld>
                <Fld lbl="Paging Devices"><NI v={v.pagingDevices} s={val=>set('pagingDevices',val)}/></Fld>
                <Fld lbl="Special Ringers"><NI v={v.specialRingers} s={val=>set('specialRingers',val)}/></Fld>
              </div>
            </SubCard>
          </div>);
        case 'numbers':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Numbers & DIDs')}{secSub('Local, toll-free, E911, SMS-enabled numbers, and international policy.')}
            <SubCard eyebrow="Numbers & DIDs">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:5 }}>
                <Fld lbl="Local DIDs" sub="$1.50/mo w/CNAM"><NI v={v.localDIDs} s={val=>set('localDIDs',val)}/></Fld>
                <Fld lbl="SMS-Enabled DIDs" sub="+$1.25/mo add-on"><NI v={v.smsDIDs} s={val=>{ set('smsDIDs',val); if(val>0 && !v.smsEnabled) set('smsEnabled',true); if(val===0) set('smsEnabled',false); }}/>{(v.smsDIDs||0)>0 && <div style={{fontSize:8,color:'#d97706',marginTop:2,fontWeight:600}}>⚡ 10DLC registration required — see SMS/10DLC section</div>}</Fld>
                <Fld lbl="Toll-Free Numbers" sub="$5.00/mo"><NI v={v.tollFreeNumbers} s={val=>set('tollFreeNumbers',val)}/></Fld>
                <Fld lbl="E911 DIDs" sub="$2.50/mo flat"><NI v={v.e911DIDs} s={val=>set('e911DIDs',val)}/></Fld>
              </div>
              <Tog on={v.tollFreePerMin} set={val=>set('tollFreePerMin',val)} lbl="Show toll-free per-minute rate" sub="$0.05/min default — metered"/>
              <Fld lbl="Numbers to Port (one-time)" sub={`$${parseFloat(settings?.voice_port_fee||6).toFixed(2)}/number`}><NI v={v.portingNumbers} s={val=>set('portingNumbers',val)}/></Fld>
            </SubCard>
            <SubCard eyebrow="International Dialing Policy">
              <div style={{ display:'grid', gap:3 }}>
                {[['none','No International Dialing','Blocked — domestic only'],['standard','Standard International','Safe countries only — client waiver required'],['open','Full Open Access','All non-high-risk countries — client waiver required']].map(([key,lbl,desc])=>(
                  <div key={key} onClick={()=>set('internationalDialing',key)} style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', border:`${v.internationalDialing===key?'2':'1'}px solid ${v.internationalDialing===key?'#dc2626':'#e5e7eb'}`, background:v.internationalDialing===key?'#fef2f2':'white' }}>
                    <div style={{ fontSize:9, fontWeight:700, color:v.internationalDialing===key?'#dc2626':'#374151' }}>{lbl}</div>
                    <div style={{ fontSize:8, color:'#9ca3af' }}>{desc}</div>
                  </div>
                ))}
              </div>
              {(v.internationalDialing==='standard'||v.internationalDialing==='open') && (<div style={{ marginTop:5, padding:'5px 8px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, fontSize:9, color:'#92400e' }}>⚠ Client waiver required — responsible for all charges including malicious traffic</div>)}
            </SubCard>
          </div>);
        case 'porting':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Number porting')}{secSub('Numbers to bring across, and LOA readiness.')}
            {(v.portingNumbers || 0) > 0 ? (<>
              <SubCard eyebrow="Numbers to Port">
                <div style={{ marginBottom:8, padding:'7px 10px', background:'#f5f3ff', border:'1px solid #ddd6fe', borderRadius:5, fontSize:9, color:'#6d28d9', lineHeight:1.6 }}>📋 List all numbers to be ported below — used to generate the Letter of Authorization (LOA).</div>
                <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Numbers Being Ported ({(v.portingDIDList||'').split('\n').filter(l=>l.trim()).length} of {v.portingNumbers} entered)</div>
                <textarea value={v.portingDIDList || ''} onChange={e => set('portingDIDList', e.target.value)} rows={Math.max(4, (v.portingNumbers || 1) + 1)} placeholder={Array.from({length: v.portingNumbers || 1}, (_,i) => `(555) 555-000${i+1}`).join('\n')} style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', resize:'vertical', outline:'none', lineHeight:1.8 }}/>
                <div style={{ fontSize:8, color:'#9ca3af', marginTop:3 }}>One number per line. Include area code.</div>
              </SubCard>
              <PortReadinessCard quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} clientEmail={recipientEmail} recipientContact={recipientContact} hubDealId={hubDealId} loaDocRecord={loaDocRecord} settings={settings} />
            </>) : secSub('No numbers being ported. Set a port quantity under Numbers & DIDs to enable the LOA workflow.')}
          </div>);
        case 'sms':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('SMS / 10DLC')}{secSub('10DLC brand registration and campaigns — required when SMS DIDs are selected.')}
            <SubCard eyebrow="SMS / 10DLC">
              {(v.smsDIDs||0) > 0 && !v.smsEnabled && (<div style={{ padding:'6px 9px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, fontSize:9, color:'#92400e', marginBottom:6, fontWeight:600 }}>⚡ Required — you have {v.smsDIDs} SMS-enabled DID{v.smsDIDs>1?'s':''}.</div>)}
              <Tog on={v.smsEnabled || (v.smsDIDs||0)>0} set={val=>{ if((v.smsDIDs||0)>0&&!val) return; set('smsEnabled',val); }} lbl="SMS / MMS Enabled" sub={(v.smsDIDs||0)>0 ? "Required — SMS DIDs selected" : "10DLC reg + campaigns + metered segments"}/>
              {(v.smsEnabled || (v.smsDIDs||0)>0) && (
                <div style={{ marginTop:6 }}>
                  <Tog on={v.smsNewRegistration!==false} set={val=>set('smsNewRegistration',val)} lbl="New 10DLC brand registration" sub="$65 one-time NRC"/>
                  <div style={{ marginTop:8 }}>
                    <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>SMS Campaigns <span style={{ fontSize:8, color:'#9ca3af', fontWeight:400 }}>— $15/mo each</span></div>
                    {(v.smsCampaignList||[]).map((c,i)=>(
                      <div key={i} style={{ display:'flex', gap:5, marginBottom:4, alignItems:'center' }}>
                        <input value={c.name||''} onChange={e=>{const l=[...(v.smsCampaignList||[])];l[i]={...l[i],name:e.target.value};set('smsCampaignList',l);}} placeholder={`Campaign ${i+1} — e.g. Notifications, Marketing`} style={{ flex:1, padding:'4px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, outline:'none' }}/>
                        <button onClick={()=>set('smsCampaignList',(v.smsCampaignList||[]).filter((_,j)=>j!==i))} style={{ padding:'3px 6px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:3, color:'#dc2626', fontSize:11, cursor:'pointer' }}>×</button>
                      </div>
                    ))}
                    <button onClick={()=>set('smsCampaignList',[...(v.smsCampaignList||[]),{name:''}])} style={{ padding:'4px 9px', background:'white', border:'1px dashed #c4b5fd', borderRadius:4, fontSize:9, color:'#7c3aed', cursor:'pointer' }}>+ Add Campaign</button>
                    {(v.smsCampaignList||[]).length > 0 && (<div style={{ marginTop:5, fontSize:9, color:'#6b7280' }}>{v.smsCampaignList.length} campaign{v.smsCampaignList.length>1?'s':''} × $15/mo = <strong>${v.smsCampaignList.length*15}/mo</strong></div>)}
                  </div>
                </div>
              )}
            </SubCard>
          </div>);
        case 'recording':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Call recording')}{secSub('Enable recording and choose the retention period.')}
            <SubCard eyebrow="Call Recording">
              <Tog on={v.callRecording} set={val=>set('callRecording',val)} lbl="Enable Call Recording" sub="$35/mo — includes 30-day storage"/>
              {v.callRecording && (
                <div style={{ marginTop:6, padding:'8px 10px', background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:5 }}>
                  <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:6 }}>Recording Retention Period</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                    {[[30,'30 days',35,'Included in base'],[60,'60 days',50,'+ 1 month storage'],[90,'90 days',65,'+ 2 months storage'],[180,'180 days',110,'+ 5 months storage'],[365,'1 year',195,'+ 11 months storage']].map(([days,label,total,note])=>(
                      <div key={days} onClick={()=>set('callRecordingDays',days)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 8px', borderRadius:4, cursor:'pointer', border:`${(v.callRecordingDays||30)===days?'2':'1'}px solid ${(v.callRecordingDays||30)===days?'#0891b2':'#e5e7eb'}`, background:(v.callRecordingDays||30)===days?'#ecfeff':'white' }}>
                        <div><span style={{ fontSize:10, fontWeight:700, color:(v.callRecordingDays||30)===days?'#0e7490':'#374151' }}>{label}</span><span style={{ fontSize:8, color:'#9ca3af', marginLeft:6 }}>{note}</span></div>
                        <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${total}/mo</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SubCard>
          </div>);
        case 'fax':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Fax & ATA')}{secSub('Virtual fax package and analog telephone adapters.')}
            {voiceFaxSecEl}
            {voiceAtaSecEl}
          </div>);
        case 'hardware':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Hardware')}{secSub('Phones — lease, purchase, or bring your own — plus the bundle discount.')}
            {voiceHardwareSecEl}
            <SubCard eyebrow="Bundle Discount"><Tog on={v.isManagedIT} set={val=>set('isManagedIT',val)} lbl="Managed IT client" sub={`${Math.round(parseFloat(settings.voice_bundle_discount||0.10)*100)}% discount on voice MRR`}/></SubCard>
          </div>);
        case 'pricing':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Pricing')}{secSub('Line-item breakdown, discounts, onboarding incentive, and the effective date.')}
            {/* v3.9.2 fast-follow: quote line items (restored from classic) */}
            {result && (
              <SubCard eyebrow="Quote line items">
                {[['seats','Billable Seats'],['devices','Non-Billable Devices (Included)'],['hybrid','3CX Licensing & Hosting'],['sip','SIP Trunking'],['numbers','Numbers & DIDs'],['fax','Virtual Fax'],['addons','Add-ons'],['sms','SMS/MMS'],['hardware','Hardware'],['onetime','One-Time Fees'],['discount','Discounts']].map(([sec, secLabel]) => {
                  const secLines = (result.lines || []).filter(l => l.section === sec);
                  if (!secLines.length) return null;
                  return (<span key={sec}><SH l={secLabel}/>{secLines.map((l, i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px', marginLeft:7 }}>
                      <div><span style={{ fontSize:9, color: l.mrr < 0 ? '#166534' : (l.mrr === 0 && !l.nrc) ? '#9ca3af' : '#6b7280' }}>{l.label}</span>{l.note && <div style={{ fontSize:8, color:'#9ca3af', fontStyle:'italic' }}>{l.note}</div>}{l.desc && <div style={{ fontSize:8, color:'#9ca3af' }}>{l.desc}</div>}</div>
                      <div style={{ textAlign:'right', flexShrink:0, marginLeft:6 }}>{l.mrr !== 0 && <div style={{ fontSize:10, fontWeight:500, fontFamily:'DM Mono, monospace', color: l.mrr < 0 ? '#166534' : '#374151' }}>{l.mrr < 0 ? `(${fmt$(-l.mrr)})` : fmt$(l.mrr)}<span style={{ fontSize:8, color:'#9ca3af' }}>/mo</span></div>}{l.nrc > 0 && <div style={{ fontSize:10, fontFamily:'DM Mono, monospace', color:'#0f766e' }}>{fmt$(l.nrc)}<span style={{ fontSize:8, color:'#9ca3af' }}> NRC</span></div>}{l.mrr === 0 && !l.nrc && !l.metered && <div style={{ fontSize:9, color:'#9ca3af' }}>{l.note || 'Included'}</div>}{l.metered && <div style={{ fontSize:8, color:'#3b82f6', fontWeight:600 }}>Metered</div>}</div>
                    </div>))}</span>);
                })}
                <div style={{ margin:'6px 0', borderTop:'2px solid #0f1e3c' }}/>
                <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px' }}><span style={{ fontSize:10, fontWeight:700, color:'#374151' }}>Monthly Recurring</span><span style={{ fontSize:12, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$(result.finalMRR)}</span></div>
                {result.nrc > 0 && (() => {
                  // v3.9.5: show the NET one-time charge (post onboarding-incentive
                  // + post one-time-discount) as the headline — the same figure the
                  // Stripe prepay + the Service Order's "Total Due Upon Signing" use.
                  // Strike the gross when an adjustment shrinks it so the discount is
                  // visible right in the line-item summary.
                  const adjusted = Math.abs((oneTimeResolved?.finalNRC ?? result.nrc) - result.nrc) > 0.005;
                  return (
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px' }}>
                      <span style={{ fontSize:10, fontWeight:600, color:'#374151' }}>One-Time Fees{adjusted && <span style={{ fontSize:8, color:'#9ca3af', fontWeight:400, marginLeft:4 }}>after adjustments</span>}</span>
                      <span style={{ textAlign:'right' }}>
                        {adjusted && <span style={{ fontSize:9, color:'#9ca3af', textDecoration:'line-through', marginRight:5, fontFamily:'DM Mono, monospace' }}>{fmt$(result.nrc)}</span>}
                        <span style={{ fontSize:11, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f766e' }}>{fmt$(oneTimeResolved?.finalNRC ?? result.nrc)}</span>
                      </span>
                    </div>
                  );
                })()}
              </SubCard>
            )}
            {onboardingNrc > 0 && (
              <OnboardingIncentive fee={onboardingNrc} marketTier="secondary" contractTerm={v.contractTerm} initialValue={obIncentive}
                onChange={inc => { const sig = inc ? `${inc.mode || 'none'}:${inc.effectiveFee ?? ''}:${inc.discountPct ?? ''}:${inc.splitMonths ?? ''}` : 'null'; const prevSig = obIncentive ? `${obIncentive.mode || 'none'}:${obIncentive.effectiveFee ?? ''}:${obIncentive.discountPct ?? ''}:${obIncentive.splitMonths ?? ''}` : 'null'; if (sig === prevSig) return; setObIncentive(inc); }} />
            )}
            {result && <ManualDiscounts value={manualDiscounts} onChange={setManualDiscounts} baseMRR={resultRaw.finalMRR} />}
            {result && <OneTimeDiscounts value={oneTimeDiscounts} onChange={setOneTimeDiscounts} bases={oneTimeBases} />}
            <AgreementEffectiveDate value={effectiveDate} onChange={setEffectiveDate} />
          </div>);
        case 'documents':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Documents & send')}{secSub('Lock rates, preview, send the Voice agreement for signature, and collect prepayment.')}
            <QuoteDocuments quoteId={existingQuote?.id} />
            <div id="oos-rate-card"><MarketRateCard quoteId={existingQuote?.id} clientZip={clientZip} fallbackMarket={selectedMkt}
              onRatesAccepted={(rates, suggestedTier) => { if (rates) setAcceptedRates(rates); if (suggestedTier && marketTiers.length) { const tier = marketTiers.find(t => t.tier_key === suggestedTier); if (tier) setSelectedMkt(tier); } }} /></div>
            {existingQuote?.id && (
              <SignatureCard docType="voice_quote" ref={signatureCardRef} quoteId={existingQuote.id} quoteStatus={quoteStatus} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} clientContact={recipientContact} clientEmail={recipientEmail} upfrontAmount={oneTimeResolved.finalNRC || 0} userId={profile?.id} ratesLocked={ratesLocked} onLockRatesPrompt={() => setShowLockRatesModal('send_signature')} />
            )}
            {result?.nrc > 0 && (
              <StripePaymentCard quoteId={existingQuote?.id} quoteType="voice" quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} clientEmail={recipientEmail} recipientContact={recipientContact} prepayAmount={oneTimeResolved.finalNRC} hubspotDealId={hubDealId} />
            )}
            {existingQuote?.id && (
              <div style={{ display: 'flex', gap: 10 }}>
                <V3Button variant="secondary" size="sm" onClick={() => setShowRateSheet(true)}>Rate sheet</V3Button>
                <V3Button variant="secondary" size="sm" onClick={() => setShowLOA(true)} disabled={(v.portingNumbers || 0) === 0}>LOA</V3Button>
              </div>
            )}
          </div>);
        case 'terms':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>
            {secTitle('Special terms')}{secSub('Addendums attached to the Voice agreement.')}
            <div style={{ fontSize: v3.font.size.body, color: v3.color.muted }}>{addendums.length} addendum{addendums.length === 1 ? '' : 's'} attached.</div>
            <div><V3Button variant="secondary" size="sm" onClick={() => setAddendumsModalOpen(true)}>Edit addendums</V3Button></div>
          </div>);
        case 'engagement':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{secTitle('Engagement')}{secSub('Client interaction with the sent quote (live).')}<EngagementPanel quoteId={existingQuote?.id} /><QuoteNotes quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} hubDealId={hubDealId} /></div>);
        case 'history':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{secTitle('History')}{secSub('Saved versions of this quote.')}<QuoteHistory quoteId={existingQuote?.id} /></div>);
        default:
          return null;
      }
    };

    const locked = isQuoteLocked(existingQuote);
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        {isReadOnly && (<div style={{ padding: '8px 24px', background: v3.color.status.amberBg, color: v3.color.status.amberText, fontSize: 12, fontWeight: 600 }}>🔒 View only — your account no longer has permission to edit Voice quotes.</div>)}
        {locked && (
          <LockedQuoteBanner notes={dealDescription} onNotesChange={setDealDescription} onSave={saveQuote} saving={saving} reason={QUOTE_LOCK_REASON} />
        )}
        <WorkspaceShell header={v3Header} sections={v3Sections} activeId={activeSection} onSelect={setVoiceFocusedSection} rail={v3Rail}>
          {locked && activeSection !== 'documents'
            ? <div style={{ pointerEvents: 'none', opacity: 0.55 }} aria-disabled="true">{renderVoiceFocus()}</div>
            : renderVoiceFocus()}
        </WorkspaceShell>
        {showRateSheet && (<RateSheetModalComp onClose={() => setShowRateSheet(false)} analysis={marketAnalysis} settings={settings} />)}
        {showLOA && (<LOAModal onClose={() => setShowLOA(false)} quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} proposalName={proposalName || recipientBiz} clientName={recipientBiz} recipientContact={recipientContact} recipientEmail={recipientEmail} serviceAddress={recipientAddress} portingDIDList={v.portingDIDList || ''} settings={settings} hubDealId={hubDealId} onDocSaved={(rec) => { setLoaDocRecord(rec); setShowLOA(false); }} />)}
        <AddendumsModal open={addendumsModalOpen} onClose={() => setAddendumsModalOpen(false)} addendums={addendums} templates={addendumTemplates} onSave={setAddendums} />
        <ServiceOrderPreviewModal open={soPreviewModalOpen} onClose={() => setSoPreviewModalOpen(false)} quoteId={existingQuote?.id} docType="voice_quote" clientBiz={recipientBiz} />
        {showLockRatesModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 480, boxShadow: v3.shadow.modal }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: v3.color.status.amberText, margin: '0 0 10px' }}>⚠ Lock OOS Rates First?</h3>
              <p style={{ fontSize: 12, color: v3.color.bodySoft, lineHeight: 1.6, margin: '0 0 14px' }}>The Out-of-Scope Rate Schedule isn&rsquo;t locked yet. Open <strong>Documents &amp; send</strong> and click <em>Accept All Rates</em> before sending.</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <V3Button variant="secondary" size="sm" onClick={() => setShowLockRatesModal(null)}>Cancel</V3Button>
                <V3Button variant="secondary" size="sm" onClick={() => { const action = showLockRatesModal; setShowLockRatesModal(null); if (action === 'send_signature' && signatureCardRef.current) signatureCardRef.current.openSendModal(); }}>Send Anyway</V3Button>
                <V3Button variant="dark" size="sm" onClick={() => { setShowLockRatesModal(null); setVoiceFocusedSection('documents'); setTimeout(scrollToOosRateCard, 60); }}>Go to Rates ↓</V3Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>
      {/* ── LEFT PANEL ── */}
      <div style={{ width:350, flexShrink:0, background:'white', borderRight:'1px solid #e5e7eb', overflowY:'auto', padding:'12px 14px' }}>

        {/* v3.5.67: read-only banner */}
        {isReadOnly && (
          <div style={{ marginBottom:8, padding:'8px 10px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:5, fontSize:11, color:'#92400e', fontWeight:600 }}>
            🔒 View only — your account no longer has permission to edit Voice quotes. Contact an admin to restore access.
          </div>
        )}

        {/* Quote badge */}
        {existingQuote && (
          <div style={{ marginBottom:8, padding:'5px 8px', background:'#f0f7ff', borderRadius:5, fontSize:11, color:'#1e40af', fontWeight:600 }}>
            {existingQuote.quote_number} — Voice
          </div>
        )}

        {/* HubSpot */}
        <HubSpotConnect
          dealId={hubDealId}
          dealUrl={hubDealUrl}
          dealName={hubDealName}
          description={dealDescription}
          onDescriptionChange={setDealDescription}
          quoteNumber={existingQuote?.quote_number}
          mrr={result?.finalMRR}
          contractValue={result ? result.finalMRR * v.contractTerm + oneTimeResolved.finalNRC : 0}
          packageName={voiceLabel}
          contractTerm={v.contractTerm}
          existingQuoteId={existingQuote?.id}
          clientName={recipientBiz}
          onConnect={full => {
            setHubDealId(full.dealId); setHubDealUrl(full.dealUrl); setHubDealName(full.deal.dealname);
            if (full.company) {
              if (full.company.name) setRecipientBiz(full.company.name);
              const addr = [full.company.address, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', ');
              if (addr) setRecipientAddress(addr);
              if (full.company.zip) {
                setClientZip(full.company.zip);
                const zr = lookupZip(full.company.zip);
                setZipResult(zr);
                if (zr) { const tier = marketTiers.find(t => t.tier_key === zr.tier); if (tier) { setSelectedMkt(tier); setZipApplied(true); } if (zr.city) setMarketCity(zr.city); if (zr.state) setMarketState(zr.state); }
              }
            } else { const x = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim(); if (x) setRecipientBiz(x); }
            if (full.contact) { const n=[full.contact.firstname,full.contact.lastname].filter(Boolean).join(' '); if(n) setRecipientContact(n); if(full.contact.email) setRecipientEmail(full.contact.email); if(full.contact.phone) setRecipientPhone(full.contact.phone); if(full.contact.mobilephone) setRecipientMobilePhone(full.contact.mobilephone); }
      // v3.5.99.7.4: capture write-back routing IDs + original-state snapshot
      if (full.contactId) setHubContactId(full.contactId);
      if (full.companyId) setHubCompanyId(full.companyId);
      setHubspotSnapshot(captureHubspotSnapshot(full));
            if (!proposalName && full.deal.dealname) setProposalName(`FerrumIT Hosted Voice — ${full.company?.name||full.deal.dealname}`);
          }}
          onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }}
        />
        <SPTConnect
          proposalId={sptProposalId}
          quoteId={existingQuote?.id}
          clientName={recipientBiz}
          quoteNumber={existingQuote?.quote_number}
          settings={settings}
          onConnect={(pid) => setSptProposalId(pid)}
          onDisconnect={() => setSptProposalId(null)}
        />

        {/* Client fields */}
        <Sec t="Proposal Details" c="#0f1e3c">
          <Fld lbl="Assigned Sales Rep">
            <RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} />
          </Fld>
          <Fld lbl="Proposal Name"><TI value={proposalName} onChange={setProposalName} placeholder="FerrumIT Hosted Voice — Acme Corp"/></Fld>
          <Fld lbl="Client Business Name"><TI value={recipientBiz} onChange={setRecipientBiz} placeholder="Acme Corp"/></Fld>
          <Grid2>
            <Fld lbl="Contact Name"><TI value={recipientContact} onChange={setRecipientContact} placeholder="Jane Smith"/></Fld>
            <Fld lbl="Contact Email"><TI value={recipientEmail} onChange={setRecipientEmail} placeholder="jane@acme.com"/></Fld>
          </Grid2>
          <Fld lbl="Business Address"><TI value={recipientAddress} onChange={setRecipientAddress} placeholder="123 Main St, Chicago, IL 60601"/></Fld>
          <Grid2>
            <Fld lbl="Office Phone"><TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567"/></Fld>
            <Fld lbl="Mobile Phone"><TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321"/></Fld>
          </Grid2>
          {/* v3.5.99.7.6: manual full-overwrite push to HubSpot */}
          {(hubContactId || hubCompanyId) && (
            <div style={{ marginTop: 6, padding: '6px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={pushAllToHubspot} disabled={hubPushing}
                style={{ padding: '4px 10px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: hubPushing ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                {hubPushing ? '⏳ Pushing...' : '↑ Push to HubSpot'}
              </button>
              <span style={{ fontSize: 9, color: '#0c4a6e' }}>Overwrites HubSpot contact + company with values above</span>
              {hubPushMsg && <span style={{ fontSize: 10, color: '#075985', flex: 1, textAlign: 'right', minWidth: 0 }}>{hubPushMsg}</span>}
            </div>
          )}
          {/* v3.5.99.0: Internal Notification CCs — see QuotePage block for rationale */}
          <div style={{ marginTop: 10, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#92400e', marginBottom: 4 }}>🔒 Internal Notifications</div>
            <Fld lbl="CC on Approval (comma-separated)"><TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com"/></Fld>
            <div style={{ fontSize: 9, color: '#92400e', marginTop: 4, lineHeight: 1.4 }}>Leave blank to use the global default from Admin → Notifications. Not visible to the customer.</div>
          </div>
          <Fld lbl="Zip Code">
            <input value={clientZip} onChange={e=>handleZipChange(e.target.value)} placeholder="60601"
              style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', outline:'none' }}/>
            {zipResult && <div style={{ fontSize:9, color:'#6b7280', marginTop:2 }}>{zipResult.name || `ZIP ${zipResult.zip}`}</div>}
          </Fld>
        </Sec>

        {/* Market Tier — matches IT quote design */}
        <Sec t="Market Tier" c="#0f1e3c">
          {/* Market status badge (v3.5.37) */}
          <div style={{ marginBottom: 6 }}>
            <MarketStatusBadge
              aiMultiplier={null}
              aiMultiplierTier={null}
              selectedMkt={selectedMkt}
              marketCity={marketCity}
              marketState={marketState}
              locked={!!pricingSnapshot}
              compact={true}
            />
          </div>
          {marketTiers.map(t => (
            <div key={t.id} onClick={() => setSelectedMkt(t)}
              style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', marginBottom:2,
                border:`${selectedMkt?.id===t.id?'2':'1'}px solid ${selectedMkt?.id===t.id?tierColor(t.tier_key):'#e5e7eb'}`,
                background:selectedMkt?.id===t.id?tierBg(t.tier_key):'white',
                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:10, fontWeight:700, color:tierColor(t.tier_key) }}>{staticTierToMILabel(t.tier_key)} <span style={{ fontWeight:400, color:'#9ca3af', fontSize:9 }}>({t.name})</span></span>
              <span style={{ fontSize:9, color:'#6b7280', fontFamily:'DM Mono, monospace' }}>
                {(t.labor_multiplier||t.pricing_multiplier||1)<1
                  ? `-${Math.round((1-(t.labor_multiplier||t.pricing_multiplier||1))*100)}% pricing`
                  : 'baseline'}
              </span>
            </div>
          ))}
        </Sec>

        {/* Contract & Status — just under Proposal Details */}
        <Sec t="Contract & Status" c="#374151">
          <Grid2>
            <Fld lbl="Contract Term">
              <select value={v.contractTerm} onChange={e=>set('contractTerm',+e.target.value)}
                style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none' }}>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
                <option value={36}>36 months</option>
              </select>
            </Fld>
            <Fld lbl="Quote Status">
              <select value={quoteStatus} onChange={e=>setQuoteStatus(e.target.value)}
                style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none' }}>
                {['draft','in_review','approved','sent','won','lost','expired'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </Fld>
          </Grid2>
        </Sec>

        {/* Quote Type */}
        <Sec t="Voice Quote Type" c="#7c3aed">
          <div style={{ display:'grid', gap:3 }}>
            {[['hosted','Hosted Voice — Per Seat','Per-user hosted PBX on our platform'],['hybrid','Hybrid Hosting — BYOPBX','Client leases 3CX + we host + SIP trunking'],['sip','SIP Trunking Only','Concurrent call paths — client has own PBX']].map(([key,lbl,desc])=>(
              <div key={key} onClick={()=>set('quoteType',key)} style={{ padding:'7px 9px', borderRadius:5, cursor:'pointer', border:`${v.quoteType===key?'2':'1'}px solid ${v.quoteType===key?'#7c3aed':'#e5e7eb'}`, background:v.quoteType===key?'#faf5ff':'white' }}>
                <div style={{ fontSize:10, fontWeight:700, color:v.quoteType===key?'#6d28d9':'#374151' }}>{lbl}</div>
                <div style={{ fontSize:9, color:'#9ca3af', marginTop:1 }}>{desc}</div>
              </div>
            ))}
          </div>
        </Sec>

        {/* ── HOSTED SEATS ── */}
        {v.quoteType === 'hosted' && (
          <>
            <Sec t="License Type" c="#2563eb">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4 }}>
                {[['pro','3CX Pro','Standard features'],['enterprise','3CX Enterprise','+ AI & transcription']].map(([key,lbl,desc])=>(
                  <div key={key} onClick={()=>set('licenseType',key)} style={{ padding:'6px 8px', borderRadius:4, cursor:'pointer', border:`${v.licenseType===key?'2':'1'}px solid ${v.licenseType===key?'#2563eb':'#e5e7eb'}`, background:v.licenseType===key?'#eff6ff':'white' }}>
                    <div style={{ fontSize:10, fontWeight:700, color:v.licenseType===key?'#1e40af':'#374151' }}>{lbl}</div>
                    <div style={{ fontSize:8, color:'#9ca3af' }}>{desc}</div>
                  </div>
                ))}
              </div>
              {recommendedTier && (
                <div style={{ marginTop:6, padding:'5px 8px', background:'#f0f7ff', borderRadius:4, fontSize:9, color:'#1e40af' }}>
                  Recommended: <strong>{recommendedTier.label}</strong> ({recommendedTier.ext_min}–{recommendedTier.ext_max} ext) — ${(recommendedTier.annual_cost/12).toFixed(2)}/mo amortized
                </div>
              )}
            </Sec>

            <Sec t="Seat Configuration" c="#0891b2">
              <Grid2>
                <Fld lbl="Billable Seats" sub="humans only"><NI v={v.seats} s={val=>set('seats',val)}/></Fld>
                <Fld lbl="Sell Price / Seat ($)"><NI v={v.seatPrice} s={val=>set('seatPrice',val)}/></Fld>
              </Grid2>
              {v.seats > 0 && settings && (
                <div style={{ marginTop:6, padding:'6px 9px', background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:4, fontSize:9, color:'#0369a1' }}>
                  <div style={{ fontWeight:700, marginBottom:3 }}>Ferrum Cost Breakdown (auto-calculated)</div>
                  {(() => {
                    const tier = getRecommendedTier(v.seats, v.licenseType||'pro', settings, cxTiersDB);
                    const lsCost = getLightsailCost(v.seats, settings);
                    const cxMo = tier ? tier.annual_cost / 12 : 0;
                    const totalCost = cxMo + lsCost;
                    const margin = v.seatPrice > 0 ? 1 - (totalCost / (v.seats * v.seatPrice)) : null;
                    return (
                      <div>
                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                          <span>3CX {tier?.label} (${ tier?.annual_cost || 0}/yr ÷ 12)</span>
                          <span style={{ fontFamily:'DM Mono, monospace' }}>${cxMo.toFixed(2)}/mo</span>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                          <span>{getLightsailLabel(v.seats, settings)}</span>
                          <span style={{ fontFamily:'DM Mono, monospace' }}>${lsCost}/mo</span>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, borderTop:'1px solid #bae6fd', marginTop:3, paddingTop:3 }}>
                          <span>Total Ferrum Cost</span>
                          <span style={{ fontFamily:'DM Mono, monospace' }}>${totalCost.toFixed(2)}/mo</span>
                        </div>
                        {margin !== null && (
                          <div style={{ display:'flex', justifyContent:'space-between', color: margin >= 0.4 ? '#065f46' : margin >= 0.25 ? '#92400e' : '#991b1b', fontWeight:700, marginTop:2 }}>
                            <span>Implied Gross Margin</span>
                            <span style={{ fontFamily:'DM Mono, monospace' }}>{(margin*100).toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </Sec>

            <Sec t="Non-Billable Devices" c="#6b7280">
              <div style={{ fontSize:9, color:'#9ca3af', marginBottom:5 }}>Shown on quote at $0 — for transparency and provisioning</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:5 }}>
                <Fld lbl="Common Area Phones"><NI v={v.commonAreaPhones} s={val=>set('commonAreaPhones',val)}/></Fld>
                <Fld lbl="Voicemail Only Ext."><NI v={v.voicemailOnly} s={val=>set('voicemailOnly',val)}/></Fld>
                <Fld lbl="Door Phones"><NI v={v.doorPhones} s={val=>set('doorPhones',val)}/></Fld>
                <Fld lbl="Paging Devices"><NI v={v.pagingDevices} s={val=>set('pagingDevices',val)}/></Fld>
                <Fld lbl="Special Ringers"><NI v={v.specialRingers} s={val=>set('specialRingers',val)}/></Fld>
              </div>
            </Sec>
          </>
        )}

        {/* ── HYBRID HOSTING ── */}
        {v.quoteType === 'hybrid' && (
          <Sec t="3CX License & Hosting" c="#2563eb">
            <Fld lbl="3CX License Tier">
              <select value={v.cxTierId} onChange={e=>set('cxTierId',e.target.value)}
                style={{ width:'100%', padding:'5px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none' }}>
                {/* v3.5.77: dropdown options rendered from cxTiersDB (or CX_TIERS fallback)
                    so admin price edits flow through without code changes. Uses structured
                    fields (paths, ext_min, ext_max, annual_cost) to build the option text
                    rather than parsing the label string. */}
                {(() => {
                  const tiers = (Array.isArray(cxTiersDB) && cxTiersDB.length > 0) ? cxTiersDB : CX_TIERS;
                  const fmtCost = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                  const proTiers = tiers.filter(t => t.type === 'pro');
                  const entTiers = tiers.filter(t => t.type === 'enterprise');
                  return (
                    <>
                      <optgroup label="3CX Pro">
                        {proTiers.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.paths} paths ({t.ext_min}–{t.ext_max} ext) — ${fmtCost(t.annual_cost)}/yr
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="3CX Enterprise">
                        {entTiers.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.paths} paths ({t.ext_min}–{t.ext_max} ext) — ${fmtCost(t.annual_cost)}/yr
                          </option>
                        ))}
                      </optgroup>
                    </>
                  );
                })()}
              </select>
            </Fld>
            <Tog on={v.clientPaysMonthly} set={val=>set('clientPaysMonthly',val)} lbl="Amortize license monthly" sub="Auto-calculates annual ÷ 12"/>
            <Tog on={v.isManagedIT} set={val=>set('isManagedIT',val)} lbl="Managed IT client" sub="Support included — hosting only charged"/>
            <Tog on={v.largerInstance} set={val=>set('largerInstance',val)} lbl="Enhanced VM instance" sub="API/reporting integrations — higher compute"/>
          </Sec>
        )}

        {/* ── SIP TRUNKING (shown for hybrid and sip-only) ── */}
        {(v.quoteType === 'sip' || v.quoteType === 'hybrid') && (
          <Sec t="SIP Trunking" c="#0891b2">
            <Fld lbl="Concurrent Call Paths" sub="$19.95/path/mo"><NI v={v.sipChannels} s={val=>set('sipChannels',val)}/></Fld>
          </Sec>
        )}

        {/* Numbers & DIDs */}
        <Sec t="Numbers & DIDs" c="#0f766e">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:5 }}>
            <Fld lbl="Local DIDs" sub="$1.50/mo w/CNAM"><NI v={v.localDIDs} s={val=>set('localDIDs',val)}/></Fld>
            <Fld lbl="SMS-Enabled DIDs" sub="+$1.25/mo add-on">
              <NI v={v.smsDIDs} s={val=>{
                set('smsDIDs',val);
                if(val>0 && !v.smsEnabled) set('smsEnabled',true);
                if(val===0) set('smsEnabled',false);
              }}/>
              {(v.smsDIDs||0)>0 && <div style={{fontSize:8,color:'#d97706',marginTop:2,fontWeight:600}}>⚡ 10DLC registration + campaigns required — see SMS/10DLC section below</div>}
            </Fld>
            <Fld lbl="Toll-Free Numbers" sub="$5.00/mo"><NI v={v.tollFreeNumbers} s={val=>set('tollFreeNumbers',val)}/></Fld>
            <Fld lbl="E911 DIDs" sub="$2.50/mo flat"><NI v={v.e911DIDs} s={val=>set('e911DIDs',val)}/></Fld>
          </div>
          <Tog on={v.tollFreePerMin} set={val=>set('tollFreePerMin',val)} lbl="Show toll-free per-minute rate" sub="$0.05/min default — metered"/>
          {v.tollFreePerMin && (
            <div style={{ marginTop:4, padding:'8px 10px', background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:5 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>Toll-Free Per-Minute Rate</div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ fontSize:10, color:'#6b7280' }}>Rate ($/min)</div>
                <input
                  type="number"
                  step="0.001"
                  min="0.019"
                  value={v.tollFreePerMinRate ?? 0.05}
                  onChange={e => {
                    const val = parseFloat(e.target.value);
                    set('tollFreePerMinRate', isNaN(val) ? 0.05 : Math.max(0.019, val));
                  }}
                  onBlur={e => {
                    const val = parseFloat(e.target.value);
                    if (isNaN(val) || val < 0.019) set('tollFreePerMinRate', 0.019);
                  }}
                  style={{ width:80, padding:'4px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', outline:'none' }}
                />
                {(v.tollFreePerMinRate ?? 0.05) <= 0.019 && (
                  <span style={{ fontSize:9, color:'#dc2626', fontWeight:600 }}>Floor rate — cannot go lower</span>
                )}
                {(v.tollFreePerMinRate ?? 0.05) > 0.019 && (v.tollFreePerMinRate ?? 0.05) !== 0.05 && (
                  <span style={{ fontSize:9, color:'#d97706', fontWeight:600 }}>Custom rate — standard is $0.05/min</span>
                )}
                {(v.tollFreePerMinRate ?? 0.05) === 0.05 && (
                  <span style={{ fontSize:9, color:'#6b7280' }}>Standard rate</span>
                )}
              </div>
              <div style={{ fontSize:8, color:'#9ca3af', marginTop:4 }}>Minimum $0.019/min. Adjust for negotiated or volume rates.</div>
            </div>
          )}
          <Fld lbl="Numbers to Port (one-time)" sub={`$${parseFloat(settings?.voice_port_fee||6).toFixed(2)}/number`}><NI v={v.portingNumbers} s={val=>set('portingNumbers',val)}/></Fld>
        </Sec>

        {/* Number Porting Detail — shown when portingNumbers > 0 */}
        {(v.portingNumbers || 0) > 0 && (
          <Sec t="Numbers to Port" c="#7c3aed">
            <div style={{ marginBottom:8, padding:'7px 10px', background:'#f5f3ff', border:'1px solid #ddd6fe', borderRadius:5, fontSize:9, color:'#6d28d9', lineHeight:1.6 }}>
              📋 List all numbers to be ported below. This information will be used to generate the Letter of Authorization (LOA) — the signed document submitted to the current carrier to authorize the port.
            </div>
            <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>
              Numbers Being Ported ({(v.portingDIDList||'').split('\n').filter(l=>l.trim()).length} of {v.portingNumbers} entered)
            </div>
            <textarea
              value={v.portingDIDList || ''}
              onChange={e => set('portingDIDList', e.target.value)}
              rows={Math.max(4, (v.portingNumbers || 1) + 1)}
              placeholder={Array.from({length: v.portingNumbers || 1}, (_,i) => `(555) 555-000${i+1}`).join('\n')}
              style={{ width:'100%', padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', resize:'vertical', outline:'none', lineHeight:1.8 }}
            />
            <div style={{ fontSize:8, color:'#9ca3af', marginTop:3 }}>One number per line. Include area code. Format: (555) 555-0100 or 5555550100 — both work.</div>
            {(v.portingDIDList||'').split('\n').filter(l=>l.trim()).length > (v.portingNumbers||0) && (
              <div style={{ marginTop:5, padding:'5px 8px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, fontSize:9, color:'#92400e' }}>
                ⚠ You have entered more numbers than the port quantity above. Update the quantity or remove extra numbers.
              </div>
            )}
          </Sec>
        )}

        {/* International Dialing */}
        <Sec t="International Dialing Policy" c="#dc2626">
          <div style={{ display:'grid', gap:3 }}>
            {[['none','No International Dialing','Blocked — domestic only'],['standard','Standard International','Safe countries only — client waiver required'],['open','Full Open Access','All non-high-risk countries — client waiver required']].map(([key,lbl,desc])=>(
              <div key={key} onClick={()=>set('internationalDialing',key)} style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', border:`${v.internationalDialing===key?'2':'1'}px solid ${v.internationalDialing===key?'#dc2626':'#e5e7eb'}`, background:v.internationalDialing===key?'#fef2f2':'white' }}>
                <div style={{ fontSize:9, fontWeight:700, color:v.internationalDialing===key?'#dc2626':'#374151' }}>{lbl}</div>
                <div style={{ fontSize:8, color:'#9ca3af' }}>{desc}</div>
              </div>
            ))}
          </div>
          {(v.internationalDialing==='standard'||v.internationalDialing==='open') && (
            <div style={{ marginTop:5, padding:'5px 8px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, fontSize:9, color:'#92400e' }}>
              ⚠ Client waiver required — responsible for all charges including malicious traffic
            </div>
          )}
        </Sec>

        {/* Fax */}
        <Sec t="Virtual Fax" c="#0891b2">

          {/* Step 1 — user + DID count drives package suggestion */}
          {/* v3.5.86.6: suggestFaxPackage now receives faxPackagesDB so its
              tier capacity comes from voice_fax_packages.included_users /
              .included_dids — same source as the picker below. Adding a new
              package via Admin → Voice Fax Packages auto-extends the
              suggestion engine without code changes. */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:8 }}>
            <Fld lbl="Fax Users" hint="How many users need fax access">
              <NI v={v.faxUsers||1} s={val=>{set('faxUsers',val); const sug=suggestFaxPackage(val,v.faxDIDs,faxPackagesDB); if(sug&&v.faxType!=='none'&&v.faxType!=='email_only') set('faxType',sug);}}/>
            </Fld>
            <Fld lbl="Fax DID Numbers" hint="Dedicated fax lines needed">
              <NI v={v.faxDIDs||1} s={val=>{set('faxDIDs',val); const sug=suggestFaxPackage(v.faxUsers,val,faxPackagesDB); if(sug&&v.faxType!=='none'&&v.faxType!=='email_only') set('faxType',sug);}}/>
            </Fld>
          </div>

          {/* Step 2 — package picker with auto-suggested badge — DB-driven from voice_fax_packages */}
          <Fld lbl="Fax Package">
            <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
              {(() => {
                const faxCatalog = getFaxPackages(faxPackagesDB);
                const opts = [{ v:'none', l:'No Fax', sell:null, cost:null, d:'' }];
                Object.entries(faxCatalog).forEach(([key, pkg]) => {
                  opts.push({ v:key, l:pkg.label, sell:pkg.price, cost:pkg.cost||0, d:pkg.desc });
                });
                return opts.map(opt => {
                  const suggested = opt.v !== 'none' && opt.v !== 'email_only' && suggestFaxPackage(v.faxUsers||1, v.faxDIDs||1, faxCatalog) === opt.v;
                  const selected  = v.faxType === opt.v;
                  const gm = opt.sell > 0 ? ((opt.sell - opt.cost) / opt.sell) * 100 : null;
                  const gmColor = gm == null ? '#6b7280' : gm >= 50 ? '#065f46' : gm >= 25 ? '#92400e' : '#991b1b';
                  return (
                    <div key={opt.v} onClick={()=>set('faxType',opt.v)}
                      style={{ padding:'6px 8px', borderRadius:4, cursor:'pointer',
                        border:`${selected?'2':'1'}px solid ${selected?'#0891b2':'#e5e7eb'}`,
                        background:selected?'#ecfeff':'white',
                        display:'flex', justifyContent:'space-between', alignItems:'center', gap:6 }}>
                      <div style={{ minWidth:0, flex:1 }}>
                        <span style={{ fontSize:10, fontWeight:700, color:selected?'#0e7490':'#374151' }}>{opt.l}</span>
                        {suggested && !selected && <span style={{ fontSize:8, marginLeft:5, padding:'1px 5px', background:'#d1fae5', color:'#065f46', borderRadius:3, fontWeight:700 }}>suggested</span>}
                        {opt.d && <div style={{ fontSize:8, color:'#6b7280', marginTop:1 }}>{opt.d}</div>}
                      </div>
                      {opt.sell != null && (
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', flexShrink:0, gap:1 }}>
                          <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${opt.sell.toFixed(2)}/mo</span>
                          {gm != null && (
                            <span style={{ fontSize:8, fontFamily:'DM Mono, monospace', color:gmColor, fontWeight:700 }}>
                              cost ${opt.cost.toFixed(2)} · {gm.toFixed(0)}% GM
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </Fld>

          {/* $0 cost warning — visible on the selected package when cost hasn't been entered yet */}
          {v.faxType && v.faxType !== 'none' && (() => {
            const fp = getFaxPackages(faxPackagesDB)[v.faxType];
            if (!fp || (fp.cost && fp.cost > 0)) return null;
            return (
              <div style={{ padding:'5px 8px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, fontSize:9, color:'#92400e', marginTop:4 }}>
                ⚠ No cost configured for this package — gross margin will read as 100%. Set the wholesale cost in Admin → Voice Fax Packages.
              </div>
            );
          })()}

          {/* Extra user/DID cost when over package limits */}
          {v.faxType && !['none','email_only','solo','team'].includes(v.faxType) && (() => {
            const fp = getFaxPackages(faxPackagesDB)[v.faxType];
            const eu = fp?.extra_user && (v.faxUsers||1) > fp.users ? (v.faxUsers - fp.users) : 0;
            const ed = fp?.extra_did  && (v.faxDIDs||1)  > fp.dids  ? (v.faxDIDs  - fp.dids)  : 0;
            if (!eu && !ed) return null;
            return (
              <div style={{ padding:'5px 8px', background:'#ecfeff', border:'1px solid #a5f3fc', borderRadius:4, fontSize:9, color:'#0e7490', marginTop:4 }}>
                {eu > 0 && <div>+ {eu} extra users × ${fp.extra_user}/mo = ${(eu * fp.extra_user).toFixed(2)}/mo</div>}
                {ed > 0 && <div>+ {ed} extra DIDs × ${fp.extra_did}/mo = ${(ed * fp.extra_did).toFixed(2)}/mo</div>}
              </div>
            );
          })()}
        </Sec>

        {/* ATA Devices — separate from fax package */}
        <Sec t="ATA Devices" c="#0369a1">
          <div style={{ fontSize:9, color:'#6b7280', marginBottom:6, lineHeight:1.5 }}>
            Analog Telephone Adapters connect legacy fax machines and analog phones to the hosted PBX.
            Hardware is a one-time purchase; service is billed monthly per device.
          </div>

          {(v.ataItems||[]).map((ata, i) => (
            <div key={i} style={{ padding:'8px', background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:5, marginBottom:6 }}>
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr auto', gap:6, alignItems:'end' }}>
                <div>
                  <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Model</div>
                  <select value={ata.modelId||'ht802'} onChange={e=>{
                    const model = hardwareCatalog.atas.find(m=>m.id===e.target.value);
                    const items = [...(v.ataItems||[])];
                    items[i] = { ...items[i], modelId: e.target.value, label: model?.label||e.target.value, hardware_nrc: model?.hardware_nrc??items[i].hardware_nrc, monthly: model?.monthly??items[i].monthly, ports: model?.ports||1 };
                    set('ataItems', items);
                  }} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>
                    {hardwareCatalog.atas.map(m=><option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Qty</div>
                  <input type="number" min="1" value={ata.qty||1}
                    onChange={e=>{const items=[...(v.ataItems||[])];items[i]={...items[i],qty:+e.target.value};set('ataItems',items);}}
                    style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, textAlign:'center', outline:'none' }}/>
                </div>
                <div>
                  <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>HW NRC</div>
                  <input type="number" min="0" step="5" value={ata.hardware_nrc??65}
                    onChange={e=>{const items=[...(v.ataItems||[])];items[i]={...items[i],hardware_nrc:+e.target.value};set('ataItems',items);}}
                    style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, textAlign:'center', outline:'none' }}/>
                </div>
                <div>
                  <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>/mo</div>
                  <input type="number" min="0" step="1" value={ata.monthly??15}
                    onChange={e=>{const items=[...(v.ataItems||[])];items[i]={...items[i],monthly:+e.target.value};set('ataItems',items);}}
                    style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, textAlign:'center', outline:'none' }}/>
                </div>
                <button onClick={()=>set('ataItems',(v.ataItems||[]).filter((_,j)=>j!==i))}
                  style={{ padding:'4px 7px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:4, color:'#dc2626', fontSize:12, cursor:'pointer', marginBottom:1 }}>×</button>
              </div>
              {ata.ports > 1 && <div style={{ fontSize:8, color:'#0369a1', marginTop:4 }}>⚡ {ata.ports}-port device — supports {ata.ports} analog lines</div>}
            </div>
          ))}

          <button onClick={()=>{
            const def = hardwareCatalog.atas[0];
            if (!def) return;
            set('ataItems',[...(v.ataItems||[]),{modelId:def.id,label:def.label,qty:1,hardware_nrc:def.hardware_nrc,monthly:def.monthly,ports:def.ports}]);
          }} style={{ padding:'5px 10px', background:'white', border:'1px dashed #93c5fd', borderRadius:4, fontSize:10, color:'#0369a1', cursor:'pointer', width:'100%', textAlign:'left' }}>
            + Add ATA Device
          </button>

          {(v.ataItems||[]).length > 0 && (() => {
            const totalNRC = (v.ataItems||[]).reduce((s,a)=>s+(parseFloat(a.hardware_nrc||0)*parseInt(a.qty||1)),0);
            const totalMRR = (v.ataItems||[]).reduce((s,a)=>s+(parseFloat(a.monthly||0)*parseInt(a.qty||1)),0);
            return (
              <div style={{ marginTop:6, display:'flex', gap:8 }}>
                {totalNRC > 0 && <div style={{ fontSize:9, padding:'3px 8px', background:'#ecfeff', border:'1px solid #a5f3fc', borderRadius:3, color:'#0e7490', fontFamily:'DM Mono, monospace' }}>Hardware NRC: ${totalNRC.toFixed(0)}</div>}
                {totalMRR > 0 && <div style={{ fontSize:9, padding:'3px 8px', background:'#ecfeff', border:'1px solid #a5f3fc', borderRadius:3, color:'#0e7490', fontFamily:'DM Mono, monospace' }}>Service MRR: ${totalMRR.toFixed(2)}/mo</div>}
              </div>
            );
          })()}
        </Sec>

        {/* SMS / 10DLC — moved up, required when smsDIDs > 0 */}
        <Sec t="SMS / 10DLC" c="#7c3aed">
          {(v.smsDIDs||0) > 0 && !v.smsEnabled && (
            <div style={{ padding:'6px 9px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, fontSize:9, color:'#92400e', marginBottom:6, fontWeight:600 }}>
              ⚡ Required — you have {v.smsDIDs} SMS-enabled DID{v.smsDIDs>1?'s':''}.
            </div>
          )}
          <Tog
            on={v.smsEnabled || (v.smsDIDs||0)>0}
            set={val=>{ if((v.smsDIDs||0)>0&&!val) return; set('smsEnabled',val); }}
            lbl="SMS / MMS Enabled"
            sub={(v.smsDIDs||0)>0 ? "Required — SMS DIDs selected" : "10DLC reg + campaigns + metered segments"}
          />
          {(v.smsEnabled || (v.smsDIDs||0)>0) && (
            <div style={{ marginTop:6 }}>
              <Tog on={v.smsNewRegistration!==false} set={val=>set('smsNewRegistration',val)} lbl="New 10DLC brand registration" sub="$65 one-time NRC"/>
              <div style={{ marginTop:8 }}>
                <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:5 }}>
                  SMS Campaigns <span style={{ fontSize:8, color:'#9ca3af', fontWeight:400 }}>— $15/mo each · quantity depends on client use case</span>
                </div>
                {(v.smsCampaignList||[]).map((c,i)=>(
                  <div key={i} style={{ display:'flex', gap:5, marginBottom:4, alignItems:'center' }}>
                    <input value={c.name||''} onChange={e=>{const l=[...(v.smsCampaignList||[])];l[i]={...l[i],name:e.target.value};set('smsCampaignList',l);}}
                      placeholder={`Campaign ${i+1} — e.g. Customer Notifications, Marketing, Support`}
                      style={{ flex:1, padding:'4px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, outline:'none' }}/>
                    <button onClick={()=>set('smsCampaignList',(v.smsCampaignList||[]).filter((_,j)=>j!==i))}
                      style={{ padding:'3px 6px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:3, color:'#dc2626', fontSize:11, cursor:'pointer' }}>×</button>
                  </div>
                ))}
                {(v.smsCampaignList||[]).length === 0 && (
                  <div style={{ fontSize:9, color:'#9ca3af', fontStyle:'italic', marginBottom:6 }}>
                    No campaigns added. Add at least one campaign for 10DLC compliance.
                  </div>
                )}
                <button onClick={()=>set('smsCampaignList',[...(v.smsCampaignList||[]),{name:''}])}
                  style={{ padding:'4px 9px', background:'white', border:'1px dashed #c4b5fd', borderRadius:4, fontSize:9, color:'#7c3aed', cursor:'pointer' }}>
                  + Add Campaign
                </button>
                {(v.smsCampaignList||[]).length > 0 && (
                  <div style={{ marginTop:5, fontSize:9, color:'#6b7280' }}>
                    {v.smsCampaignList.length} campaign{v.smsCampaignList.length>1?'s':''} × $15/mo = <strong>${v.smsCampaignList.length*15}/mo</strong>
                  </div>
                )}
              </div>
              <div style={{ marginTop:6, padding:'5px 8px', background:'#f5f3ff', border:'1px solid #ddd6fe', borderRadius:4, fontSize:8, color:'#6b7280', lineHeight:1.6 }}>
                Usage rate: $0.02/SMS segment · $0.04/MMS segment — billed monthly in arrears
              </div>
            </div>
          )}
        </Sec>

        {/* Call Recording */}
        <Sec t="Call Recording" c="#0891b2">
          <Tog on={v.callRecording} set={val=>set('callRecording',val)} lbl="Enable Call Recording" sub="$35/mo — includes 30-day storage"/>
          {v.callRecording && (
            <div style={{ marginTop:6, padding:'8px 10px', background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:5 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:6 }}>Recording Retention Period</div>
              <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                {[[30,'30 days',35,'Included in base'],[60,'60 days',50,'+ 1 month storage'],[90,'90 days',65,'+ 2 months storage'],[180,'180 days',110,'+ 5 months storage'],[365,'1 year',195,'+ 11 months storage']].map(([days,label,total,note])=>(
                  <div key={days} onClick={()=>set('callRecordingDays',days)}
                    style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 8px', borderRadius:4, cursor:'pointer',
                      border:`${(v.callRecordingDays||30)===days?'2':'1'}px solid ${(v.callRecordingDays||30)===days?'#0891b2':'#e5e7eb'}`,
                      background:(v.callRecordingDays||30)===days?'#ecfeff':'white' }}>
                    <div>
                      <span style={{ fontSize:10, fontWeight:700, color:(v.callRecordingDays||30)===days?'#0e7490':'#374151' }}>{label}</span>
                      <span style={{ fontSize:8, color:'#9ca3af', marginLeft:6 }}>{note}</span>
                    </div>
                    <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${total}/mo</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:8, color:'#9ca3af', marginTop:5 }}>Base rate $35/mo includes 30-day retention. Extended storage adds $15/mo per additional 30-day period.</div>
            </div>
          )}
        </Sec>

        {/* Hardware */}
        <Sec t="Hardware" c="#374151">
          {/* Hardware type — mutually exclusive */}
          <Fld lbl="Hardware Option">
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {[['none','No Hardware — Apps only'],['lease','Evergreen Lease (monthly recurring)'],['purchase','Outright Purchase (one-time)'],['byoh','BYOH — Bring Your Own Handset']].map(([val,lbl]) => (
                <label key={val} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', padding:'4px 6px', borderRadius:4, border:`1px solid ${v.hardwareType===val?'#374151':'#e5e7eb'}`, background:v.hardwareType===val?'#f8fafc':'white' }}>
                  <input type="radio" name="hwType" value={val} checked={v.hardwareType===val} onChange={()=>set('hardwareType',val)} style={{ accentColor:'#374151' }}/>
                  <span style={{ fontSize:10, fontWeight:v.hardwareType===val?700:400, color:'#374151' }}>{lbl}</span>
                </label>
              ))}
            </div>
          </Fld>

          {/* Yealink mixed model builder */}
          {(v.hardwareType==='lease'||v.hardwareType==='purchase') && (
            <>
              <Fld lbl="Phone Models — add as many as needed">
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {(v.hardwareItems||[]).map((item,i) => (
                    <div key={i} style={{ display:'flex', gap:5, alignItems:'center' }}>
                      <select value={item.model} onChange={e=>{const items=[...(v.hardwareItems||[])];items[i]={...items[i],model:e.target.value};set('hardwareItems',items);}}
                        style={{ flex:1, padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>
                        {hardwareCatalog.yealink
                          .filter(m => v.hardwareType==='lease' ? m.lease_eligible !== false : m.purchase_eligible !== false)
                          .map(m=><option key={m.id} value={m.id}>{m.label} — {v.hardwareType==='lease'?`$${m.monthly}/mo`:`$${m.nrc}`} · {m.desc}</option>)}
                      </select>
                      <input type="number" min="1" value={item.qty||1} onChange={e=>{const items=[...(v.hardwareItems||[])];items[i]={...items[i],qty:+e.target.value};set('hardwareItems',items);}}
                        style={{ width:52, padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', textAlign:'center', outline:'none' }}/>
                      <button onClick={()=>{const items=(v.hardwareItems||[]).filter((_,j)=>j!==i);set('hardwareItems',items);}}
                        style={{ padding:'3px 6px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:3, color:'#dc2626', fontSize:12, cursor:'pointer' }}>×</button>
                    </div>
                  ))}
                  <button onClick={()=>set('hardwareItems',[...(v.hardwareItems||[]),{model:'T33G',qty:1}])}
                    style={{ padding:'4px 8px', background:'white', border:'1px dashed #d1d5db', borderRadius:4, fontSize:10, color:'#6b7280', cursor:'pointer', textAlign:'left' }}>
                    + Add phone model
                  </button>
                </div>
              </Fld>
              {v.hardwareType==='purchase' && (() => {
                // v3.5.86.5: hardware discount tier UI now reads from
                // pricing_settings (voice_hardware_free_term /
                // voice_hardware_disc_term / voice_hardware_disc_pct).
                // Defaults preserve prior behavior: free at 36mo, optional
                // 50% off at 24mo.
                //
                // Bug fix shipped here too: prior to v3.5.86.5 the toggle
                // rendered unconditionally, so a quote at 36mo showed
                // BOTH the "Apply 50% hardware discount (24-month)" toggle
                // AND the "free with 36-month contract" banner. Toggle now
                // only renders at the optional-discount term; banner only
                // at the free term — they're mutually exclusive states.
                const hwFreeTerm = parseInt(settings?.voice_hardware_free_term, 10);
                const hwDiscTerm = parseInt(settings?.voice_hardware_disc_term, 10);
                const hwDiscPct  = parseFloat(settings?.voice_hardware_disc_pct);
                const freeTerm   = Number.isFinite(hwFreeTerm) ? hwFreeTerm : 36;
                const discTerm   = Number.isFinite(hwDiscTerm) ? hwDiscTerm : 24;
                const discPctNum = Number.isFinite(hwDiscPct)  ? hwDiscPct  : 0.5;
                const discPct    = Math.round(discPctNum * 100);
                const atFreeTerm = v.contractTerm === freeTerm;
                const atDiscTerm = v.contractTerm === discTerm;
                return (
                  <>
                    {atDiscTerm && (
                      <Tog on={v.hardwareDiscount50} set={val=>set('hardwareDiscount50',val)} lbl={`Apply ${discPct}% hardware discount`} sub={`${discTerm}-month contract discount`}/>
                    )}
                    {atFreeTerm && (
                      <div style={{ padding:'4px 7px', background:'#dcfce7', borderRadius:4, fontSize:9, color:'#166534', marginTop:3 }}>✓ Hardware included free with {freeTerm}-month contract</div>
                    )}
                    <div style={{ marginTop:6, padding:'5px 8px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, fontSize:9, color:'#92400e' }}>
                      📦 Shipping — billed at end of implementation at then-current UPS rates. Not quoted upfront.
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {/* BYOH */}
          {v.hardwareType==='byoh' && (
            <Fld lbl="BYOH Devices">
              <div style={{ fontSize:9, color:'#6b7280', marginBottom:8, lineHeight:1.5 }}>
                Search the 3CX compatibility database below. Each device will be wiped and registered at ${parseFloat(settings?.voice_byoh_fee||20).toFixed(0)}/device NRC.
                Devices not in the database are flagged as unverified.
              </div>
              <ByohPicker
                devices={v.byohItems||[]}
                onChange={items => set('byohItems', items)}
              />
              {(v.byohItems||[]).reduce((s,i)=>s+parseInt(i.qty||0),0) > 0 && (
                <div style={{ marginTop:6, padding:'5px 8px', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:4, fontSize:9, color:'#1e40af' }}>
                  Total BYOH fee: {(v.byohItems||[]).reduce((s,i)=>s+parseInt(i.qty||0),0)} devices × ${parseFloat(settings?.voice_byoh_fee||20).toFixed(0)} = ${((v.byohItems||[]).reduce((s,i)=>s+parseInt(i.qty||0),0) * parseFloat(settings?.voice_byoh_fee||20)).toFixed(0)} NRC
                </div>
              )}
            </Fld>
          )}
        </Sec>

        {/* Bundle discount */}
        <Sec t="Bundle Discount" c="#166534">
          <Tog on={v.isManagedIT} set={val=>set('isManagedIT',val)} lbl="Managed IT client" sub={`${Math.round(parseFloat(settings.voice_bundle_discount||0.10)*100)}% discount on voice MRR`}/>
        </Sec>



        {/* Save */}
        <QuoteSaveBar
          onSave={saveQuote}
          existingQuote={existingQuote}
          quoteStatus={quoteStatus}
          setQuoteStatus={setQuoteStatus}
          quoteType="voice"
          saving={saving}
          saveMsg={saveMsg}
          saveButtonPadding="7px"
          reviewRowMarginTop={5}
          extraButtons={
            <button
              onClick={() => navigate('/bundle/new', { state: { fromQuote: {
                type: 'voice',
                clientName: recipientBiz, clientZip,
                proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc,
                notes: dealDescription,
                hubDealId, hubDealUrl, hubDealName,
                voiceInputs: { ...v },
              }}})}
              style={{ padding:'6px 10px', background:'#faf5ff', border:'1px solid #ddd6fe', borderRadius:4, fontSize:11, color:'#6d28d9', fontWeight:600, cursor:'pointer' }}>
              📦 Bundle with IT
            </button>
          }
        />
      </div>

      {/* ── RIGHT PANEL ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#f8fafc', minWidth:0 }}>
        <ReviewBanner
          quote={{ ...existingQuote, status: quoteStatus, hubspot_deal_id: hubDealId }}
          quoteType="voice"
          onStatusChange={s => setQuoteStatus(s)}
        />
        {/* ── Price Lock Banner ── */}
        {pricingSnapshot && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 14px', background:'#1e3a5f', borderBottom:'1px solid #2d4f7a' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:14 }}>🔒</span>
              <div>
                <span style={{ fontSize:11, fontWeight:700, color:'#93c5fd' }}>Prices locked</span>
                <span style={{ fontSize:10, color:'#64748b', marginLeft:6 }}>as of {priceLockDate ? new Date(priceLockDate).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : 'approval'}</span>
              </div>
            </div>
            {(profile?.id === repId || profile?.role === 'admin') && (
              <button onClick={() => setShowUnlockModal(true)}
                style={{ fontSize:10, padding:'3px 10px', background:'rgba(255,255,255,0.1)', color:'#93c5fd', border:'1px solid #2d4f7a', borderRadius:3, cursor:'pointer', fontWeight:600 }}>
                Unlock Pricing
              </button>
            )}
          </div>
        )}
        {showUnlockModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
            <div style={{ background:'white', borderRadius:10, padding:28, width:440, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:'0 0 10px' }}>⚠ Unlock Pricing</h3>
              <p style={{ fontSize:12, color:'#374151', lineHeight:1.6, margin:'0 0 14px' }}>Pricing was locked when this quote was approved. Unlocking allows rates to update from current package pricing.</p>
              <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:5, padding:'8px 12px', fontSize:11, color:'#991b1b', marginBottom:18 }}>
                If this quote has been sent or exported to Smart Pricing Table, unlocking may cause price discrepancies. Consider a new revision instead.
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button onClick={() => setShowUnlockModal(false)} style={{ padding:'7px 16px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, cursor:'pointer' }}>Cancel</button>
                <button onClick={async () => {
                  setPricingSnapshot(null); setPriceLockDate(null); setShowUnlockModal(false);
                  if (existingQuote?.id) await supabase.from('quotes').update({ pricing_snapshot: null, price_locked_at: null, price_locked_by: null }).eq('id', existingQuote.id);
                }} style={{ padding:'7px 18px', background:'#dc2626', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer' }}>Unlock & Recalculate</button>
              </div>
            </div>
          </div>
        )}

        {/* ── v3.5.83.2.1: Lock-Rates-First Banner ──
            Renders when the quote is saved but OOS rates haven't been locked
            yet. Suppressed once pricingSnapshot exists (locking after approval
            would be too late). The "Jump to OOS Rates ↓" button scrolls
            straight to the MarketRateCard via the id="oos-rate-card" anchor. */}
        {existingQuote?.id && !pricingSnapshot && !ratesLocked && (
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'8px 14px', background:'#fef3c7', borderBottom:'1px solid #fbbf24',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:14 }}>⚠️</span>
              <div>
                <span style={{ fontSize:11, fontWeight:700, color:'#92400e' }}>Lock OOS rates before sending</span>
                <span style={{ fontSize:10, color:'#78350f', marginLeft:6 }}>
                  Click <strong>Accept All Rates</strong> on the Out-of-Scope Rate Schedule card to capture current pricing for this client&rsquo;s market. Required before Voice Service Agreement generation.
                </span>
              </div>
            </div>
            <button onClick={scrollToOosRateCard}
              style={{ fontSize:10, padding:'4px 10px', background:'#92400e', color:'white', border:'none', borderRadius:3, cursor:'pointer', fontWeight:600 }}>
              Jump to OOS Rates ↓
            </button>
          </div>
        )}

        {/* ── v3.5.83.2.1: Lock-Rates-First Confirmation Modal ──
            Renders when the rep clicks Send (in any of VoiceSignatureCard's
            three Send buttons) while OOS rates aren't locked. Three actions:
              - "Lock Rates Now"   → close + scroll to MarketRateCard
              - "Send Anyway"      → close + signatureCardRef.openSendModal()
                                     (the imperative-handle escape hatch)
              - "Cancel"           → just close */}
        {showLockRatesModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
            <div style={{ background:'white', borderRadius:10, padding:28, width:480, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#92400e', margin:'0 0 10px' }}>⚠ Lock OOS Rates First?</h3>
              <p style={{ fontSize:12, color:'#374151', lineHeight:1.6, margin:'0 0 14px' }}>
                The Out-of-Scope Rate Schedule isn&rsquo;t locked yet. The Voice Service Agreement can&rsquo;t be generated until OOS rates are locked.
              </p>
              <div style={{ background:'#fef3c7', border:'1px solid #fbbf24', borderRadius:5, padding:'10px 12px', fontSize:11, color:'#78350f', marginBottom:18, lineHeight:1.5 }}>
                <strong>Recommended:</strong> click <em>Lock Rates Now</em>, scroll to the Out-of-Scope Rate Schedule card, and click <em>Accept All Rates</em>. Then return here and click Send again.
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', flexWrap:'wrap' }}>
                <button onClick={() => setShowLockRatesModal(null)}
                  style={{ padding:'7px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, cursor:'pointer' }}>
                  Cancel
                </button>
                <button onClick={() => {
                  // "Send Anyway" — close modal, then trigger SignatureCard's
                  // send modal directly via the imperative ref. The backend
                  // (v3.5.82.4.1) will still reject with rates_not_locked if
                  // they really aren't locked at sign-attempt time, but this
                  // override lets reps proceed when they have a business
                  // reason to send pre-lock (rare).
                  setShowLockRatesModal(null);
                  signatureCardRef.current?.openSendModal();
                }} style={{ padding:'7px 14px', background:'#fee2e2', border:'1px solid #fca5a5', color:'#991b1b', borderRadius:5, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  Send Anyway
                </button>
                <button onClick={() => {
                  setShowLockRatesModal(null);
                  setTimeout(scrollToOosRateCard, 50);
                }} style={{ padding:'7px 18px', background:'#0f1e3c', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  Lock Rates Now ↓
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ flex:1, overflowY:'auto', padding:'14px 16px' }}>
        {!result
          ? <div style={{ textAlign:'center', color:'#9ca3af', marginTop:80, fontSize:12 }}>Enter quote details to generate voice pricing</div>
          : <div className="fade-in">

              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                    <div style={{ padding:'2px 8px', background:'#7c3aed', color:'white', borderRadius:3, fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Voice</div>
                    <h2 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:0 }}>{proposalName || recipientBiz || 'Voice Quote'}</h2>
                  </div>
                  <div style={{ fontSize:10, color:'#6b7280' }}>
                    {v.quoteType === 'hosted' ? `Hosted Voice · ${v.licenseType === 'enterprise' ? 'Enterprise' : 'Pro'}` : v.quoteType === 'hybrid' ? 'Hybrid Hosting (BYOPBX)' : 'SIP Trunking Only'} · {v.contractTerm}-month term
                    {v.isManagedIT && <span style={{ color:'#166534', marginLeft:4, fontWeight:600 }}>· Bundle discount applied</span>}
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:8, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.04em' }}>Monthly Recurring</div>
                  <div style={{ fontSize:22, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$0(result.finalMRR)}</div>
                </div>
              </div>

              {/* KPI cards */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:7, marginBottom:10 }}>
                {[['Monthly MRR',fmt$0(result.finalMRR),'#0f1e3c','#f0f4ff'],['One-Time Fees',fmt$0(oneTimeResolved.finalNRC),'#0f766e','#f0fdf4'],['Implied GM',fmtPct(result.gm),gc,gb],['Est. w/Tax',fmt$0(result.finalMRR + result.estTax),'#7c3aed','#faf5ff']].map(([l,val,co,bg])=>(
                  <div key={l} style={{ background:bg, borderRadius:5, padding:'7px 6px', textAlign:'center' }}>
                    <div style={{ fontSize:7, fontWeight:600, color:'#6b7280', letterSpacing:'.05em', textTransform:'uppercase', marginBottom:2 }}>{l}</div>
                    <div style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono, monospace', color:co }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* ── Crossover Analysis ── */}
              {crossover && v.quoteType === 'hosted' && (
                <div style={{ marginBottom:10, padding:'12px 14px', background: crossover.totalMRR < result.finalMRR ? '#fef3c7' : '#f0fdf4', border:`1px solid ${crossover.totalMRR < result.finalMRR ? '#fde68a' : '#86efac'}`, borderRadius:7 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                    <div style={{ fontSize:11, fontWeight:700, color: crossover.totalMRR < result.finalMRR ? '#92400e' : '#166534' }}>
                      {crossover.totalMRR < result.finalMRR ? '⚡ Hybrid Hosting would save client money' : '✓ Per-seat pricing is more competitive'}
                    </div>
                    <div style={{ fontSize:10, color:'#6b7280' }}>Crossover analysis</div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div style={{ padding:'8px 10px', background:'white', borderRadius:5, border:'1px solid #e5e7eb' }}>
                      <div style={{ fontSize:9, color:'#6b7280', marginBottom:2 }}>This quote — Per Seat</div>
                      <div style={{ fontSize:16, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$0(result.finalMRR)}/mo</div>
                    </div>
                    <div style={{ padding:'8px 10px', background:'white', borderRadius:5, border:'1px solid #e5e7eb' }}>
                      <div style={{ fontSize:9, color:'#6b7280', marginBottom:2 }}>Hybrid Hosting option</div>
                      <div style={{ fontSize:16, fontWeight:700, fontFamily:'DM Mono, monospace', color: crossover.totalMRR < result.finalMRR ? '#92400e' : '#374151' }}>{fmt$0(crossover.totalMRR)}/mo</div>
                      <div style={{ fontSize:8, color:'#9ca3af', marginTop:1 }}>{crossover.tier.label} · ${crossover.licMonthly.toFixed(2)} lic + ${crossover.hostSell} hosting + SIP</div>
                    </div>
                  </div>
                  {crossover.totalMRR < result.finalMRR && (
                    <div style={{ marginTop:6, fontSize:10, color:'#92400e' }}>
                      Consider switching to Hybrid Hosting — saves client <strong>{fmt$0(result.finalMRR - crossover.totalMRR)}/mo</strong>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {/* Line items */}
                <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6 }}>Quote Line Items</div>

                  {/* Group by section */}
                  {[
                    ['seats',     'Billable Seats'],
                    ['devices',   'Non-Billable Devices (Included)'],
                    ['hybrid',    '3CX Licensing & Hosting'],
                    ['sip',       'SIP Trunking'],
                    ['numbers',   'Numbers & DIDs'],
                    ['fax',       'Virtual Fax'],
                    ['addons',    'Add-ons'],
                    ['sms',       'SMS/MMS'],
                    ['hardware',  'Hardware'],
                    ['onetime',   'One-Time Fees'],
                    ['discount',  'Discounts'],
                  ].map(([sec, secLabel]) => {
                    const secLines = result.lines.filter(l => l.section === sec);
                    if (!secLines.length) return null;
                    return (
                      <span key={sec}>
                        <SH l={secLabel}/>
                        {secLines.map((l, i) => (
                          <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px', marginLeft:7 }}>
                            <div>
                              <span style={{ fontSize:8, color: l.mrr < 0 ? '#166534' : l.mrr === 0 && !l.nrc ? '#9ca3af' : '#6b7280' }}>{l.label}</span>
                              {l.note && <div style={{ fontSize:7, color:'#9ca3af', fontStyle:'italic' }}>{l.note}</div>}
                              {l.desc && <div style={{ fontSize:7, color:'#9ca3af' }}>{l.desc}</div>}
                            </div>
                            <div style={{ textAlign:'right', flexShrink:0, marginLeft:6 }}>
                              {l.mrr !== 0 && <div style={{ fontSize:9, fontWeight:500, fontFamily:'DM Mono, monospace', color: l.mrr < 0 ? '#166534' : '#374151' }}>{l.mrr < 0 ? `(${fmt$(-l.mrr)})` : fmt$(l.mrr)}<span style={{ fontSize:7, color:'#9ca3af' }}>/mo</span></div>}
                              {l.nrc > 0 && <div style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#0f766e' }}>{fmt$(l.nrc)}<span style={{ fontSize:7, color:'#9ca3af' }}> NRC</span></div>}
                              {l.mrr === 0 && !l.nrc && !l.metered && <div style={{ fontSize:8, color:'#9ca3af' }}>{l.note || 'Included'}</div>}
                              {l.metered && <div style={{ fontSize:7, color:'#3b82f6', fontWeight:600 }}>Metered</div>}
                            </div>
                          </div>
                        ))}
                      </span>
                    );
                  })}

                  <div style={{ margin:'5px 0', borderTop:'2px solid #0f1e3c' }}/>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px' }}>
                    <span style={{ fontSize:9, fontWeight:700, color:'#374151' }}>Monthly Recurring</span>
                    <span style={{ fontSize:11, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$(result.mrr)}</span>
                  </div>
                  {result.bundleDiscount > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px' }}>
                      <span style={{ fontSize:9, color:'#166534' }}>Bundle Discount</span>
                      <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#166534' }}>({fmt$(result.bundleDiscount)})</span>
                    </div>
                  )}
                  {result.nrc > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px' }}>
                      <span style={{ fontSize:9, fontWeight:600, color:'#374151' }}>One-Time Fees{Math.abs(oneTimeResolved.finalNRC - result.nrc) > 0.005 && <span style={{ fontSize:7, color:'#9ca3af', fontWeight:400, marginLeft:3 }}>after adjustments</span>}</span>
                      <span style={{ fontSize:10, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f766e' }}>{Math.abs(oneTimeResolved.finalNRC - result.nrc) > 0.005 && <span style={{ color:'#9ca3af', textDecoration:'line-through', marginRight:4, fontWeight:400 }}>{fmt$(result.nrc)}</span>}{fmt$(oneTimeResolved.finalNRC)}</span>
                    </div>
                  )}
                  {/* Final MRR highlight */}
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 7px', background:'#dcfce7', borderRadius:4, marginTop:4 }}>
                    <span style={{ fontSize:9, fontWeight:700, color:'#166534' }}>✦ Final Monthly MRR</span>
                    <span style={{ fontSize:12, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#166534' }}>{fmt$(result.finalMRR)}</span>
                  </div>
                </div>

                {/* Right column */}
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>

                  {/* Tax estimate */}
                  <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6 }}>Estimated Taxes & Fees</div>
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px', marginBottom:4 }}>
                      <span style={{ fontSize:9, color:'#6b7280' }}>Voice MRR</span>
                      <span style={{ fontSize:9, fontFamily:'DM Mono, monospace' }}>{fmt$(result.finalMRR)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px', marginBottom:4 }}>
                      <span style={{ fontSize:9, color:'#6b7280' }}>Est. taxes ({Math.round(result.taxRate * 100)}%)</span>
                      <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#7c3aed' }}>~{fmt$(result.estTax)}</span>
                    </div>
                    <div style={{ margin:'5px 0', borderTop:'1px solid #e5e7eb' }}/>
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 6px', background:'#faf5ff', borderRadius:4 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:'#6d28d9' }}>Est. Total w/ Tax</span>
                      <span style={{ fontSize:12, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#6d28d9' }}>{fmt$(result.finalMRR + result.estTax)}</span>
                    </div>
                    <div style={{ fontSize:8, color:'#9ca3af', marginTop:5, lineHeight:1.5 }}>
                      * Tax estimate only. Actual taxes vary by jurisdiction (typically 20–30% for voice services). Final invoice will reflect actual regulatory fees, USF contributions, and local taxes.
                    </div>
                  </div>

                  {/* Cost model */}
                  <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6 }}>Cost Model</div>
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'2px 2px' }}>
                      <span style={{ fontSize:9, color:'#6b7280' }}>Estimated cost</span>
                      <span style={{ fontSize:9, fontFamily:'DM Mono, monospace' }}>{fmt$(result.totalCost)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 6px', background:gb, borderRadius:4, marginTop:4 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:gc }}>Implied Gross Margin</span>
                      <span style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono, monospace', color:gc }}>{fmtPct(result.gm)}</span>
                    </div>
                    {result.gm < 0.35 && <div style={{ marginTop:4, fontSize:9, color:'#92400e', background:'#fef3c7', padding:'3px 5px', borderRadius:3 }}>⚠ Below 35% — review pricing.</div>}
                  </div>

                  {/* Multi-term Contract Value */}
                  <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:8 }}>Contract Value by Term</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6 }}>
                      {[12, 24, 36].map(term => {
                        const tcv = result.finalMRR * term + oneTimeResolved.finalNRC;
                        const costTcv = result.totalCost * term + result.nrc;  // cost basis stays gross — a one-time discount compresses margin
                        const termGM = tcv > 0 ? 1 - (costTcv / tcv) : 0;
                        return (
                          <div key={term} style={{ padding:'7px 8px', background: (v.contractTerm||12)===term?'#f0f4ff':'#f8fafc', borderRadius:4, border:`1px solid ${(v.contractTerm||12)===term?'#c7d2fe':'#e5e7eb'}` }}>
                            <div style={{ fontSize:8, fontWeight:700, color:'#6b7280', textTransform:'uppercase', marginBottom:3 }}>{term}mo</div>
                            <div style={{ fontSize:11, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>${tcv.toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0})}</div>
                            <div style={{ fontSize:8, color:'#9ca3af', marginTop:1 }}>TCV</div>
                            <div style={{ fontSize:8, color: termGM>=0.40?'#166534':termGM>=0.30?'#92400e':'#991b1b', fontWeight:600, marginTop:2 }}>{(termGM*100).toFixed(1)}% GM</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ fontSize:8, color:'#9ca3af', marginTop:6 }}>
                      TCV = MRR × term + NRC · includes 3CX license + Lightsail hosting costs in margin
                    </div>
                  </div>

                  {/* International dialing policy */}
                  {v.internationalDialing !== 'none' && (
                    <div style={{ background:'#fef2f2', borderRadius:6, border:'1px solid #fecaca', padding:11 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:'#dc2626', marginBottom:4 }}>
                        {v.internationalDialing === 'standard' ? '⚠ Standard International Dialing' : '⚠ Full Open International Access'}
                      </div>
                      <div style={{ fontSize:9, color:'#7f1d1d', lineHeight:1.5 }}>
                        Client waiver required. Client accepts full financial responsibility for all charges including malicious traffic originating from their system regardless of hosting location.
                      </div>
                    </div>
                  )}

                  {/* Deal summary — matches QuotePage position */}
                  <div style={{ background:'#0f1e3c', borderRadius:6, padding:11 }}>
                    <div style={{ fontSize:8, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', color:'#475569', marginBottom:6 }}>Deal Summary</div>
                    {[
                      ['Quote #', existingQuote?.quote_number || 'Unsaved'],
                      ['Client', recipientBiz],
                      recipientContact && ['Contact', recipientContact],
                      ['Type', v.quoteType === 'hosted' ? 'Hosted Voice' : v.quoteType === 'hybrid' ? 'Hybrid Hosting' : 'SIP Trunking'],
                      v.quoteType === 'hosted' && ['Seats', v.seats],
                      v.sipChannels > 0 && ['SIP Channels', v.sipChannels],
                      ['Contract', `${v.contractTerm} months`],
                      ['Monthly MRR', fmt$0(result.finalMRR)],
                      result.nrc > 0 && ['One-Time Fees', fmt$0(oneTimeResolved.finalNRC)],
                      ['Est. w/ Tax', fmt$0(result.finalMRR + result.estTax)],
                      hubDealId && ['HubSpot', hubDealName || `#${hubDealId}`],
                    ].filter(Boolean).map(([k, val]) => (
                      <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid #1e3a5f' }}>
                        <span style={{ fontSize:9, color:'#64748b' }}>{k}</span>
                        <span style={{ fontSize:9, fontWeight:600, color:'white', fontFamily: typeof val === 'number' || (typeof val === 'string' && val.startsWith('$')) ? 'DM Mono, monospace' : 'inherit' }}>{val}</span>
                      </div>
                    ))}
                  </div>

                  {/* Port Readiness — only shown when porting numbers */}
                  {(v.portingNumbers || 0) > 0 && (
                    <PortReadinessCard
                      quoteId={existingQuote?.id}
                      quoteNumber={existingQuote?.quote_number}
                      clientName={recipientBiz}
                      clientEmail={recipientEmail}
                      recipientContact={recipientContact}
                      hubDealId={hubDealId}
                      loaDocRecord={loaDocRecord}
                      settings={settings}
                    />
                  )}

                  {/* Market Rate Analysis — same position as IT/Bundle quotes */}
                  {/* v3.5.83.2.1: id="oos-rate-card" anchor for the Lock-Rates-First
                      guard's "Lock Rates Now" modal action and banner "Jump to OOS
                      Rates ↓" button. scrollToOosRateCard targets this wrapper. */}
                  <div id="oos-rate-card">
                  <MarketRateCard
                    quoteId={existingQuote?.id}
                    clientZip={clientZip}
                    fallbackMarket={selectedMkt}
                    onRatesAccepted={(rates, suggestedTier) => {
                      // v3.5.83.2.1: capture accepted rates so ratesLocked
                      // flips true and the lock-rates banner/modal/button
                      // decoration clear. MarketRateCard re-fires this on
                      // mount when quote_rate_sheets has a row, so reopening
                      // a quote with previously-locked rates restores state.
                      if (rates) setAcceptedRates(rates);
                      if (suggestedTier && marketTiers.length) {
                        const tier = marketTiers.find(t => t.tier_key === suggestedTier);
                        if (tier) setSelectedMkt(tier);
                      }
                    }}
                  />
                  </div>

                  {/* Voice Documents */}
                  <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:'10px 12px', marginBottom:10 }}>
                    <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6b7280', marginBottom:8 }}>📄 Documents</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                      {/* v3.5.99.5: Preview Service Order — opens the full SO PDF inline */}
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#f8fafc', borderRadius:4, border:'1px solid #e5e7eb' }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                            <span style={{ fontSize:11 }}>👁</span>
                            <span style={{ fontSize:11, fontWeight:600, color:'#0f1e3c' }}>Preview Service Order</span>
                          </div>
                          <div style={{ fontSize:9, color:'#9ca3af', marginTop:1, paddingLeft:16 }}>
                            See the full document the client will sign — rendered PDF includes all sections, rates, and addendums
                          </div>
                        </div>
                        <button onClick={() => setSoPreviewModalOpen(true)}
                          style={{ padding:'4px 10px', background:'#0f1e3c', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
                          Preview
                        </button>
                      </div>
                      {/* Rate Sheet — Out-of-Scope Rates */}
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#f8fafc', borderRadius:4, border:'1px solid #e5e7eb' }}>
                        <div>
                          <div style={{ fontSize:11, fontWeight:600, color:'#0f1e3c' }}>💲 Out-of-Scope Rate Schedule</div>
                          <div style={{ fontSize:9, color:'#9ca3af', marginTop:1 }}>Market-adjusted T&M rates — applies to all labor outside contracted scope</div>
                        </div>
                        <button onClick={() => setShowRateSheet(true)}
                          style={{ padding:'4px 10px', background:'#0f1e3c', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
                          View / Export
                        </button>
                      </div>

                      {/* Assumptions & Exclusions */}
                      {/* v3.5.86.10: card now reflects saved/missing state
                          using the same teal/amber visual vocabulary
                          v3.5.86.3.1 established for IT/Multi-Site/Bundle
                          (§7o). Voice's modal stays separate
                          (VoiceAssumptionsModal — it asks Voice-specific
                          questions, not the IT ones); only the doc card
                          UI is being aligned. Pattern: amber + ⚠ when
                          story is empty; teal + ✓ Saved badge when story
                          has content. Click handler unchanged. */}
                      {(() => {
                        const complete = !!(voiceAssumptionsStory && voiceAssumptionsStory.trim().length > 0);
                        const tint = complete
                          ? { bg: '#f0fdfa', border: '#99f6e4', titleColor: '#0f766e', subtitle: 'Hosted Voice notes saved · ready to export' }
                          : { bg: '#fef3c7', border: '#fde68a', titleColor: '#92400e', subtitle: 'Hosted Voice specific — seats, porting, features, infrastructure' };
                        return (
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background: tint.bg, borderRadius:4, border:`1px solid ${tint.border}` }}>
                            <div>
                              <div style={{ fontSize:11, fontWeight:600, color: tint.titleColor, display:'flex', alignItems:'center', gap:5 }}>
                                <span>{complete ? '✓' : '⚠'}</span>
                                <span>📋 Assumptions & Exclusions</span>
                                {complete && (
                                  <span style={{ padding:'1px 5px', borderRadius:3, fontSize:8, fontWeight:700, background:'#ccfbf1', color:'#0f766e' }}>Saved</span>
                                )}
                              </div>
                              <div style={{ fontSize:9, color:'#6b7280', marginTop:1 }}>{tint.subtitle}</div>
                            </div>
                            <button onClick={() => setShowVoiceAssumptions(true)}
                              style={{ padding:'4px 10px', background: tint.titleColor, color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
                              Open
                            </button>
                          </div>
                        );
                      })()}
                      {/* v3.5.99.4.1: Special Terms (Addendums) — opens AddendumsModal.
                          Same row pattern as Out-of-Scope Rate Schedule above. Count badge
                          renders inline with the title when addendums are attached. */}
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#f8fafc', borderRadius:4, border:'1px solid #e5e7eb' }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                            <span style={{ fontSize:11 }}>📎</span>
                            <span style={{ fontSize:11, fontWeight:600, color:'#0f1e3c' }}>Special Terms</span>
                            {addendums.length > 0 && (
                              <span style={{ fontSize:8, fontWeight:700, color:'#1e40af', background:'#dbeafe', padding:'1px 5px', borderRadius:3 }}>
                                {addendums.length} attached
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize:9, color:'#9ca3af', marginTop:1, paddingLeft:16 }}>
                            {addendums.length > 0
                              ? `${addendums.length} addendum${addendums.length === 1 ? '' : 's'} attached \u00b7 renders in the Service Order PDF`
                              : 'Optional addendum templates (Supersedes, M2M renewal, ETF waiver, etc.)'}
                          </div>
                        </div>
                        <button onClick={() => setAddendumsModalOpen(true)}
                          style={{ padding:'4px 10px', background:'#0f1e3c', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
                          {addendums.length > 0 ? 'Edit' : 'Add'}
                        </button>
                      </div>

                      {/* Letter of Authorization — only shown when porting numbers */}
                      {(v.portingNumbers || 0) > 0 && (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#eef2ff', borderRadius:4, border:'1px solid #c7d2fe' }}>
                          <div>
                            <div style={{ fontSize:11, fontWeight:600, color:'#3730a3' }}>📜 Letter of Authorization</div>
                            <div style={{ fontSize:9, color:'#9ca3af', marginTop:1 }}>
                              {v.portingNumbers} number{v.portingNumbers > 1 ? 's' : ''} to port — client signature required before submitting to carrier
                            </div>
                          </div>
                          <button onClick={() => setShowLOA(true)}
                            style={{ padding:'4px 10px', background:'#3730a3', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
                            Open LOA
                          </button>
                        </div>
                      )}

                      {v.internationalDialing !== 'none' ? (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background: intlWaiverDocRecord ? '#f0fdf4' : '#fef2f2', borderRadius:4, border: `1px solid ${intlWaiverDocRecord ? '#a7f3d0' : '#fecaca'}` }}>
                          <div style={{ minWidth:0, flex:1 }}>
                            <div style={{ fontSize:11, fontWeight:600, color: intlWaiverDocRecord ? '#065f46' : '#991b1b' }}>
                              {intlWaiverDocRecord ? '✓' : '⚠'} International Dialing Waiver
                            </div>
                            {intlWaiverDocRecord ? (
                              <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:3, flexWrap:'wrap' }}>
                                {(intlWaiverDocRecord.status === 'signed' || intlWaiverDocRecord.signed_at) ? (
                                  <span style={{ fontSize:9, fontWeight:600, color:'#065f46', background:'#d1fae5', padding:'1px 6px', borderRadius:3 }}>SIGNED</span>
                                ) : (
                                  <span style={{ fontSize:9, fontWeight:600, color:'#92400e', background:'#fef3c7', padding:'1px 6px', borderRadius:3 }}>SENT — AWAITING SIGNATURE</span>
                                )}
                                {(intlWaiverDocRecord.hubspot_uploaded_at || intlWaiverDocRecord.hubspot_file_id) && (
                                  <span style={{ fontSize:9, fontWeight:600, color:'#9a3412', background:'#ffedd5', padding:'1px 6px', borderRadius:3 }}>↗ HUBSPOT</span>
                                )}
                              </div>
                            ) : (
                              <div style={{ fontSize:9, color:'#9ca3af', marginTop:1 }}>Required — client must sign before international calling is enabled</div>
                            )}
                          </div>
                          <button onClick={() => setShowIntlWaiver(true)} style={{ padding:'4px 10px', background: intlWaiverDocRecord ? '#065f46' : '#7c1d1d', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
                            {intlWaiverDocRecord ? 'View Status' : 'Open Waiver'}
                          </button>
                        </div>
                      ) : (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#f8fafc', borderRadius:4, border:'1px dashed #d1d5db' }}>
                          <div>
                            <div style={{ fontSize:11, fontWeight:600, color:'#9ca3af' }}>International Dialing Waiver</div>
                            <div style={{ fontSize:9, color:'#d1d5db' }}>Enable international dialing above to unlock · or create manually</div>
                          </div>
                          <button onClick={() => setShowIntlWaiver(true)} style={{ padding:'4px 10px', background:'#f3f4f6', color:'#9ca3af', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer' }}>Create</button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* v3.5.66: Voice signature card — sends Voice Service Agreement
                      for client signature. Visible whenever the quote has a
                      saved id (so the doc has somewhere to land in
                      signwellDocuments). Inside the card itself, the Send
                      button is disabled until quote_status === 'approved'. */}
                  {existingQuote?.id && (
                    <SignatureCard docType="voice_quote"
                      ref={signatureCardRef}
                      quoteId={existingQuote.id}
                      quoteStatus={quoteStatus}
                      quoteNumber={existingQuote?.quote_number}
                      clientName={recipientBiz}
                      clientContact={recipientContact}
                      clientEmail={recipientEmail}
                      upfrontAmount={oneTimeResolved.finalNRC || 0}
                      userId={profile?.id}
                      ratesLocked={ratesLocked}
                      onLockRatesPrompt={() => setShowLockRatesModal('send_signature')}
                    />
                  )}

                  {/* v3.5.86.4: Onboarding Incentive picker for Voice.
                      v3.5.86.5: scoped to labor-only — hardware has its own
                      discount logic driven by contract term (free at 36mo,
                      optional 50% off at 24mo, both settings-driven).
                      v3.5.92.2 §4a: NARROWED further to Programming &
                      Configuration only (was previously the broader
                      laborNrc = nrc - hardwareNrc which also included
                      Number Porting carrier fees and license/10DLC
                      activation pass-through). Porting and activation are
                      vendor/regulatory pass-through, not Ferrum labor —
                      reps shouldn't be able to discount them via the
                      onboarding picker. Picker base is result.onboardingNrc;
                      effective labor + (porting + activation + hardware)
                      all unchanged flow into the Stripe total.
                      Picker renders only when there's labor to discount.
                      marketTier hardcoded to 'secondary' (voice is hosted —
                      no geographic tier modifier). */}
                  {onboardingNrc > 0 && (
                    <OnboardingIncentive
                      fee={onboardingNrc}
                      marketTier="secondary"
                      contractTerm={v.contractTerm}
                      initialValue={obIncentive}
                      onChange={inc => {
                        // Match the dedup pattern QuotePage uses to avoid
                        // spurious revisions on every render — only commit
                        // when the meaningful fields actually changed.
                        const sig = inc ? `${inc.mode || 'none'}:${inc.effectiveFee ?? ''}:${inc.discountPct ?? ''}:${inc.splitMonths ?? ''}` : 'null';
                        const prevSig = obIncentive ? `${obIncentive.mode || 'none'}:${obIncentive.effectiveFee ?? ''}:${obIncentive.discountPct ?? ''}:${obIncentive.splitMonths ?? ''}` : 'null';
                        if (sig === prevSig) return;
                        setObIncentive(inc);
                      }}
                    />
                  )}

                  {/* v3.5.99.7.23: Manual discount lines (% or $/mo + note) */}
                  {result && (
                    <ManualDiscounts
                      value={manualDiscounts}
                      onChange={setManualDiscounts}
                      baseMRR={resultRaw.finalMRR}
                    />
                  )}
                  {result?.manualDiscount?.total > 0 && (() => {
                    const targetGM = parseFloat(settings?.target_gross_margin);
                    if (!isFinite(targetGM) || (result.gm ?? 1) >= targetGM) return null;
                    return (
                      <div style={{ marginTop: 6, fontSize: 10, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '5px 8px' }}>
                        ⚠ Manual discount drops gross margin below target ({fmtPct(result.gm)} &lt; {fmtPct(targetGM)}). You can still send — review pricing if this wasn&rsquo;t intended.
                      </div>
                    );
                  })()}

                  {/* v3.7.8: one-time (non-recurring) discounts — targeted per
                      NRC category (Onboarding / Hardware / Activation / Porting). */}
                  {result && (
                    <OneTimeDiscounts
                      value={oneTimeDiscounts}
                      onChange={setOneTimeDiscounts}
                      bases={oneTimeBases}
                    />
                  )}

                  {/* v3.5.99.7.24: optional back-dated agreement effective date */}
                  <AgreementEffectiveDate value={effectiveDate} onChange={setEffectiveDate} />

                  {/* Stripe prepayment card — Voice prepay = total NRC. */}
                  {/* v3.5.86.4: respect the onboarding incentive.
                      v3.5.92.2 §4a: prepay total = (effective programming
                      labor) + porting + activation + hardware. Porting and
                      activation now flow through separately rather than being
                      lumped into the discountable labor base. */}
                  {result?.nrc > 0 && (
                    <StripePaymentCard
                      quoteId={existingQuote?.id}
                      quoteType="voice"
                      quoteNumber={existingQuote?.quote_number}
                      clientName={recipientBiz}
                      clientEmail={recipientEmail}
                      recipientContact={recipientContact}
                      prepayAmount={oneTimeResolved.finalNRC}
                      hubspotDealId={hubDealId}
                    />
                  )}

                  {showRateSheet && (
                    <RateSheetModalComp
                      onClose={() => setShowRateSheet(false)}
                      analysis={marketAnalysis}
                      settings={settings}
                      clientName={recipientBiz}
                      recipientContact={recipientContact}
                      quoteId={existingQuote?.id}
                      quoteNumber={existingQuote?.quote_number}
                      sptProposalId={sptProposalId}
                      onSPTLinked={(pid) => { setSptProposalId(pid); if (pid && existingQuote?.id) supabase.from('quotes').update({ spt_proposal_id: pid }).eq('id', existingQuote.id); }}
                    />
                  )}

                  {showLOA && (
                    <LOAModal
                      onClose={() => setShowLOA(false)}
                      quoteId={existingQuote?.id}
                      quoteNumber={existingQuote?.quote_number}
                      proposalName={proposalName || recipientBiz}
                      clientName={recipientBiz}
                      recipientContact={recipientContact}
                      recipientEmail={recipientEmail}
                      serviceAddress={recipientAddress}
                      portingDIDList={v.portingDIDList || ''}
                      settings={settings}
                      hubDealId={hubDealId}
                      onDocSaved={(rec) => { setLoaDocRecord(rec); setShowLOA(false); }}
                    />
                  )}

                  {showVoiceAssumptions && (
                    <VoiceAssumptionsModal
                      onClose={() => setShowVoiceAssumptions(false)}
                      quoteId={existingQuote?.id}
                      quoteNumber={existingQuote?.quote_number}
                      clientName={recipientBiz}
                      recipientContact={recipientContact}
                      inputs={v}
                      voicePlan={v.quoteType}
                      settings={settings}
                      /* v3.5.86.10: also update page-level voiceAssumptionsStory
                         so the doc card reflects the saved state without a
                         page reload. Modal already passes the saved object
                         as the callback argument. */
                      onSaved={(va) => {
                        setVoiceAssumptionsStory(va?.story || '');
                        setShowVoiceAssumptions(false);
                      }}
                    />
                  )}

                  {showIntlWaiver && (
                    <IntlDialingWaiver
                      onClose={() => setShowIntlWaiver(false)}
                      quoteId={existingQuote?.id}
                      quoteNumber={existingQuote?.quote_number}
                      proposalName={proposalName || recipientBiz}
                      clientName={recipientBiz}
                      recipientContact={recipientContact}
                      recipientEmail={recipientEmail}
                      recipientAddress={recipientAddress}
                      settings={settings}
                      hubDealId={hubDealId}
                      selectedTier={v.internationalDialing !== 'none' ? v.internationalDialing : 'standard'}
                      existingDocRecord={intlWaiverDocRecord}
                      onDocSaved={(rec) => { setIntlWaiverDocRecord(rec); }}
                    />
                  )}

                  {/* v3.5.99.6: client engagement panel — auto-hides on quotes with no proposal/SO activity yet */}
                  <EngagementPanel quoteId={existingQuote?.id} />
                  {/* v3.5.99.7.13: ETF Calculator — visible only on saved quotes
                      (existingQuote?.id present means the quote was loaded/saved). */}
                  {existingQuote?.id && (
                    <button onClick={() => setShowEtfModal(true)}
                      style={{ width:'100%', marginBottom:10, padding:'8px 12px', background:'white', border:'1px solid #e5e7eb', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600, color:'#0f1e3c', textAlign:'left', display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ fontSize:14 }}>💰</span>
                      <span style={{ flex:1 }}>Calculate Early Termination Fee</span>
                      <span style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>→</span>
                    </button>
                  )}
                  {showEtfModal && (
                    <ETFCalculatorModal
                      onClose={() => setShowEtfModal(false)}
                      defaultMRR={result?.finalMRR || ''}
                      defaultTerm={v?.contractTerm || 36}
                      defaultClientName={recipientBiz}
                      defaultClientAddress={recipientAddress}
                      defaultClientContact={recipientContact}
                      defaultClientEmail={recipientEmail}
                      defaultContractReference={existingQuote?.quote_number || ''}
                      quoteId={existingQuote?.id}
                      hubDealId={hubDealId}
                    />
                  )}

                  {/* Quote Notes */}
                  <QuoteNotes
                    quoteId={existingQuote?.id}
                    quoteNumber={existingQuote?.quote_number}
                    clientName={recipientBiz}
                    hubDealId={hubDealId}
                  />
                  <QuoteHistory quoteId={existingQuote?.id} />

                </div>
              </div>
            </div>
        }
        </div>{/* end inner scroll */}
      </div>

      {/* v3.5.99.4: Special Terms (Addendums) modal */}
      <AddendumsModal
        open={addendumsModalOpen}
        onClose={() => setAddendumsModalOpen(false)}
        addendums={addendums}
        templates={addendumTemplates}
        onSave={setAddendums}
      />
      {/* v3.5.99.5: Service Order preview modal */}
      <ServiceOrderPreviewModal
        open={soPreviewModalOpen}
        onClose={() => setSoPreviewModalOpen(false)}
        quoteId={existingQuote?.id}
        docType="voice_quote"
        clientBiz={recipientBiz}
      />
    </div>
  );
}

function Sec({t,c,children}){return(<div style={{marginBottom:10}}><div style={{display:'flex',alignItems:'center',gap:4,marginBottom:5,paddingBottom:3,borderBottom:'1px solid #f1f5f9'}}><div style={{width:2,height:11,background:c||'#2563eb',borderRadius:2}}/><span style={{fontSize:9,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:'#6b7280'}}>{t}</span></div>{children}</div>);}
function Grid2({children}){return <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5}}>{children}</div>;}
function Fld({lbl,sub,children}){return(<div style={{marginBottom:4}}><label style={{display:'block',fontSize:9,fontWeight:600,color:'#374151',marginBottom:1}}>{lbl}{sub&&<span style={{fontWeight:400,color:'#9ca3af',marginLeft:3,fontSize:9}}>{sub}</span>}</label>{children}</div>);}
function TI({value,onChange,placeholder}){return<input value={value||''} onChange={e=>onChange(e.target.value)} placeholder={placeholder||''} style={{width:'100%',padding:'4px 7px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,outline:'none',color:'#374151'}}/>;}
function NI({v,s}){return<input type="number" value={v} min={0} onChange={e=>s(+e.target.value)} style={{width:'100%',padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,fontFamily:'DM Mono, monospace',color:'#1e3a5f',background:'#eff6ff',fontWeight:600,outline:'none'}}/>;}
function Tog({on,set,lbl,sub}){return(<div onClick={()=>set(!on)} style={{display:'flex',alignItems:'center',gap:7,padding:'5px 7px',borderRadius:4,cursor:'pointer',border:`1px solid ${on?'#93c5fd':'#e5e7eb'}`,background:on?'#eff6ff':'white',marginBottom:2}}><div style={{width:24,height:14,borderRadius:7,flexShrink:0,background:on?'#2563eb':'#d1d5db',position:'relative'}}><div style={{position:'absolute',top:2,left:on?12:2,width:10,height:10,borderRadius:'50%',background:'white',transition:'left .12s'}}/></div><div><span style={{fontSize:10,fontWeight:600,color:on?'#1e40af':'#374151'}}>{lbl}</span>{sub&&<span style={{fontSize:9,color:'#9ca3af',marginLeft:4}}>{sub}</span>}</div></div>);}
function SH({l}){return<div style={{fontSize:8,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'#9ca3af',padding:'4px 2px 1px',marginTop:3}}>{l}</div>;}
